import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  PLATFORM_ID,
  Inject,
  AfterViewInit,
  OnInit,
  OnDestroy,
  signal,
  computed,
  viewChild,
  NgZone,
} from "@angular/core";
import { RouterLink, RouterOutlet } from "@angular/router";
import { LayoutComponent } from "../layout/layout.component";
import {
  VaultService,
  FOLDER_LOGO_TYPES,
  FOLDER_LOGO_MAX_SIZE,
  BULK_DELETE_CHUNK_SIZE,
  BULK_FEATURE_CHUNK_SIZE,
  VIDEO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  ALLOWED_MEDIA_EXTENSIONS,
  isAllowedMediaExtension,
} from "src/app/service/vault.service";
import {
  alertModalDelete,
  alertModalWithoutConfirm,
  alertToast,
} from "../../helpers/alert";
import { Modal } from "bootstrap";
import axios from "axios";
import { CommonModule } from "@angular/common";
import { NgxDaterangepickerMd } from "ngx-daterangepicker-material";
import { Router, ActivatedRoute } from "@angular/router";
import { environment } from "src/environments/environment";
import { MatIcon } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import moment from "moment";
import Swiper from "swiper";
import { Navigation, Pagination, Keyboard } from "swiper/modules";
import { ChatService } from "src/app/service/chat.service";
import { AuthService } from "src/app/service/auth-service";
import {
  ImageCropperComponent,
  ImageCroppedEvent,
} from "ngx-image-cropper";
import { Subject, Subscription, debounceTime } from "rxjs";
import { ChangeDetectorRef } from "@angular/core";
import { NavigationStart } from "@angular/router";



// TanStack Virtual imports
import { injectVirtualizer, Virtualizer } from "@tanstack/angular-virtual";

Swiper.use([Navigation, Pagination, Keyboard]);

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_CHUNK_SIZE = 10 * 1024 * 1024;

/**
 * One in-flight S3 multipart upload. Kept in memory for the lifetime of the
 * upload so that a pause/resume continues the SAME multipart upload instead of
 * requesting a fresh uploadId and leaving holes in the part list.
 */
interface VaultUploadSession {
  file: File;
  uploadId: string;
  uniqueFileName: string;
  mediaId: string;
  urls: any[];
  totalChunks: number;
  /** Index-aligned with the chunks; parts[i] is the ETag for chunk i. */
  parts: any[];
  /** First chunk that still has to be uploaded. */
  nextChunk: number;
  tempId: string | null;
  percent: number;
  completed: boolean;
}

@Component({
  selector: "app-vault",
  imports: [
    LayoutComponent,
    RouterOutlet,
    RouterLink,
    CommonModule,
    NgxDaterangepickerMd,
    MatIcon,
    FormsModule,
    ImageCropperComponent,
  ],
  templateUrl: "./vault.component.html",
  styleUrl: "./vault.component.css"
})
export class VaultComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("createAlbumModal") createmodalElement: any;
  private modalInstance!: Modal;
  showUploadModal: boolean = false;
  @ViewChild("uploadModal") uploadModalElement: any;
  uploadModalInstance: any;

  readonly VIDEO_EXTENSIONS = VIDEO_EXTENSIONS;
  readonly IMAGE_EXTENSIONS = IMAGE_EXTENSIONS;
  readonly ALLOWED_MEDIA_EXTENSIONS = ALLOWED_MEDIA_EXTENSIONS;
  readonly allowedMediaAccept: string = 'image/*,video/*,' + ALLOWED_MEDIA_EXTENSIONS.join(',');

  isValidMediaFile(file: File | string): boolean {
    return isAllowedMediaExtension(file);
  }

  // TanStack Virtual elements
  scrollElement = viewChild<ElementRef<HTMLDivElement>>("scrollElement");
  foldersSection = viewChild<ElementRef<HTMLDivElement>>("foldersSection");

  /**
   * Height of the folders block, which shares the scroll container and sits
   * above the virtualised list. The virtualizer needs it as scrollMargin so
   * its offsets stay in step with what is actually on screen.
   */
  foldersOffset = signal(0);

  // Virtualizer setup
  virtualGridData = signal<any[]>([]);
  virtualizer = injectVirtualizer(() => ({
    scrollElement: this.scrollElement()?.nativeElement,
    count: this.virtualGridData().length,
    estimateSize: (index: number) => {
      const item = this.virtualGridData()[index];
      if (item?.type === "header") return 45;
      // Each virtual row is its own grid, so the CSS row-gap never applies
      // between rows — the gap is whatever this leaves over the card. 190
      // was under the card's real height, so rows ran together.
      return this.isMobileView ? 200 : 215;
    },
    // Each item is a whole row of cards, so 5 meant ~70 extra cards in the
    // DOM on top of what is visible.
    overscan: 2,
    scrollMargin: this.foldersOffset(),
  }));

  /**
   * Remeasures the folders block. Called after anything that can change its
   * height: loading children, switching tab, opening another folder, resizing.
   */
  private measureFoldersOffset() {
    requestAnimationFrame(() => {
      const el = this.foldersSection()?.nativeElement;
      const next = el ? el.offsetHeight : 0;

      if (next === this.foldersOffset()) return;

      this.foldersOffset.set(next);
      this.virtualizer._willUpdate();
      this.cdr.detectChanges();
    });
  }

  /** Every subscription this component owns, torn down in ngOnDestroy. */
  private subs = new Subscription();
  private placeholderMap: Map<string, { monthKey: string; tempId: string }> =
    new Map();
  private uploadSessions = new Map<string, VaultUploadSession>();
  private lastStateWrite = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private chatService: ChatService,
    private vaultService: VaultService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    window.addEventListener("dragover", this.preventGlobalDrop, false);
    window.addEventListener("drop", this.preventGlobalDrop, false);
    this.detectPageRefresh();
    this.checkScreenSize(true);
  }

  uploaderID: any;
  allMedia: any[] = [];

  isMobileView: boolean = false;

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.checkScreenSize();
    if (this.groupedMedia && this.groupedMedia.length > 0) {
      this.updateVirtualGridData();
    }
    // The folder cards reflow at a different width, changing the block height.
    this.measureFoldersOffset();
  }

  private checkScreenSize(isInitial: boolean = false) {
    this.isMobileView = window.innerWidth <= 768;
    if (!isInitial) {
      this.cdr.detectChanges();
    }
  }

  /** Header logo — replaces the Home link the removed icon rail carried. */
  goHome() {
    this.router.navigate(["/home"]);
  }

  /** Leaves the open folder. State is torn down by the queryParams handler. */
  goBackToFolders() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        folder: null,
        folderName: null,
      },
      queryParamsHandling: "merge",
    });
  }

  ngAfterViewInit() { }

  private detectPageRefresh() {
    this.isPageRefresh = !this.router.navigated;

    this.subs.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.isPageRefresh = !this.router.navigated;
        }
      })
    );
  }

  private isPageRefresh = false;
  isProcessingMedia: boolean = false;

  ngOnInit() {
    // SOCKET LISTENER FOR MEDIA PROCESSING UPDATES
    this.subs.add(
      this.chatService.vaultMediaResponse$.subscribe(
      (response: any) => {
        this.zone.run(() => {
          console.log("VAULT-MEDIA-RESPONSE RECEIVED:", response);
          const data = response?.data?.returnvalue?.data;
          if (!data || !data.mediaId) {
            console.warn("VAULT-MEDIA-RESPONSE data invalid or missing mediaId:", response);
            return;
          }

          const mediaId = data.mediaId;
          const newCompressedUrl = data.compressedImgUrl;
          const newThumbUrl = data.thumbnailUrl;
          const newDuration = data.duration;

          console.log("PROCESSING COMPLETED FOR MEDIA ID:", mediaId);

          // groupedMedia (for gallery)
          for (const group of this.groupedMedia) {
            const item = group.items.find((m) => m._id === mediaId);
            if (item) {
              console.log("ITEM FOUND IN GROUPED MEDIA:", item);

              if (item.mediaType === "video" && newThumbUrl) {
                item.thumbnailUrl = newThumbUrl;
              }
              if (item.mediaType === "image" && newCompressedUrl) {
                item.compressedImgUrl = newCompressedUrl;
              }
              if (newCompressedUrl && !item.mediaType) {
                item.compressedImgUrl = newCompressedUrl;
              }
              if (newDuration) {
                item.duration = newDuration;
              }

              item.isProcessing = false;
              break;
            }
          }

          // FULLSCREEN VIEW
          const mediaIndex = this.allMedia.findIndex((m) => m._id === mediaId);
          if (mediaIndex !== -1) {
            const file = this.allMedia[mediaIndex];

            if (file.mediaType === "video" && newThumbUrl) {
              file.thumbnailUrl = newThumbUrl;
            }
            if (file.mediaType === "image" && newCompressedUrl) {
              file.compressedImgUrl = newCompressedUrl;
            }
            if (newCompressedUrl && !file.mediaType) {
              file.compressedImgUrl = newCompressedUrl;
            }
            if (newDuration) {
              file.duration = newDuration;
            }

            file.isProcessing = false;
            this.allMedia[mediaIndex] = { ...file };
          }

          if (this.selectedMedia && this.selectedMedia._id === mediaId) {
            if (newCompressedUrl) {
              this.selectedMedia.compressedImgUrl = newCompressedUrl;
            }
            if (newThumbUrl) {
              this.selectedMedia.thumbnailUrl = newThumbUrl;
            }
            if (newDuration) {
              this.selectedMedia.duration = newDuration;
            }
            this.selectedMedia.isProcessing = false;
          }

          this.checkIfAnyMediaProcessing();
          this.updateVirtualGridData();
          this.cdr.detectChanges();
        });
      }
      )
    );

    // RESET UPLOAD STATE
    if (this.isPageRefresh) {
      this.resetUploadStateForRefresh();
    } else {
      // For internal navigation, restore the state
      this.restoreUploadState();
    }

    this.selectedDateRange = null;

    this.getAllAlbums();

    this.subs.add(
      this.albumSearch$
        .pipe(debounceTime(400))
        .subscribe((query) => this.runAlbumSearch(query))
    );

    this.subs.add(
      this.mediaSearch$
        .pipe(debounceTime(400))
        .subscribe((query) => this.executeSearch(query))
    );

    // Single entry point for opening a folder, so a deep link, a sidebar
    // click and a breadcrumb click all take the same path.
    this.subs.add(
      this.route.queryParams.subscribe((params) => {
        const folderId = params["folder"];

        if (!folderId) {
          this.clearSelectedFolder();
          return;
        }

        if (this.selectedAlbum?._id === folderId) return;

        this.openFolderById(folderId);
      })
    );

    this.uploaderID = localStorage.getItem("guestID");

    // Featuring is admin-only; the claim decides whether the control renders
    // at all, rather than letting the user hit a 400.
    this.isVaultAdmin =
      this.authService.getTokenPayload()?.department === "ADMIN";

    window.addEventListener("beforeunload", this.boundBeforeUnloadHandler);
  }

  private checkIfAnyMediaProcessing() {
    const anyProcessing =
      this.groupedMedia?.some((group) =>
        group.items.some((item) => item.isProcessing)
      ) ?? false;

    this.isProcessingMedia = anyProcessing;
  }


  private resetUploadStateForRefresh() {
    this.showUploadBox = false;
    this.uploadSuccess = false;
    this.isUploading = false;
    this.isUploadPaused = false;
    this.uploadedCount = 0;
    this.totalFiles = 0;
    this.overallProgress = 0;
    this.currentFileProgress = 0;
    this.currentFileName = '';
    this.uploadProgress = {};
    this.uploadSessions.clear();

    localStorage.removeItem('vaultUploadState');
  }

  // PREVENT FILE DROP GLOBALLY(ON RELOAD)
  /**
   * Stable reference so addEventListener/removeEventListener match. Calling
   * .bind(this) at each call site produced two different functions, so the
   * listener was never actually removed.
   */
  private boundBeforeUnloadHandler = (event: BeforeUnloadEvent) =>
    this.beforeUnloadHandler(event);

  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (this.showUploadBox && !this.uploadSuccess && this.overallProgress < 100) {
      event.preventDefault();

      event.returnValue = true;

      return "Files are currently being uploaded. If you leave, the upload may be interrupted.";
    }
  }

  // PREVENT GLOBAL DROP
  createModalInstance: any;
  ngOnDestroy(): void {
    this.revokeLogoPreview();
    this.subs.unsubscribe();
    window.removeEventListener("beforeunload", this.boundBeforeUnloadHandler);
    window.removeEventListener("dragover", this.preventGlobalDrop, false);
    window.removeEventListener("drop", this.preventGlobalDrop, false);
  }

  // PROGRESS
  /**
   * Overall progress is the mean of every session's real byte progress.
   * (There used to be a second, simulated writer that ticked +0.5%/s and
   * fought this value; it has been removed.)
   */
  private recalculateOverallProgress() {
    const sessions = [...this.uploadSessions.values()];
    if (!sessions.length) return;

    const sum = sessions.reduce((acc, s) => acc + s.percent, 0);
    this.overallProgress = Math.floor(sum / sessions.length);
  }

  // ── FOLDER CREATE / RENAME MODAL ────────────────────────────────────────
  maxLengthExceeded = false;
  duplicateAlbumError = false;

  folderModalMode: "create" | "edit" = "create";
  /** Set when creating a subfolder; null when creating a root folder. */
  folderModalParent: any = null;
  /** Folder being renamed, in edit mode. */
  folderModalTarget: any = null;
  folderNameValue = "";
  folderLogoFile: File | null = null;
  folderLogoPreview: string | null = null;
  folderLogoError = "";
  isSavingFolder = false;

  readonly folderLogoTypes = FOLDER_LOGO_TYPES;
  readonly folderLogoMaxSize = FOLDER_LOGO_MAX_SIZE;
  readonly folderLogoAccept = FOLDER_LOGO_TYPES.join(",");

  openCreateFolderModal(parent: any = null, event?: MouseEvent) {
    event?.stopPropagation();

    if (parent && !this.canAddSubFolder(parent)) return;

    this.folderModalMode = "create";
    this.folderModalParent = parent;
    this.folderModalTarget = null;
    this.resetFolderModalState();
    this.showFolderModal();
  }

  openRenameFolderModal(folder: any, event?: MouseEvent) {
    event?.stopPropagation();
    if (!this.isFolderOwner(folder)) return;

    this.folderModalMode = "edit";
    this.folderModalParent = null;
    this.folderModalTarget = folder;
    this.resetFolderModalState();
    this.folderNameValue = folder.folderName || "";
    this.folderLogoPreview = folder.folderLogo || null;
    this.showFolderModal();
  }

  get folderModalTitle(): string {
    if (this.folderModalMode === "edit") return "Rename Folder";
    return this.folderModalParent ? "Create Subfolder" : "Create Folder";
  }

  /** Renaming reads the target's own nesting; creating reads the parent. */
  get folderNamePlaceholder(): string {
    const isSubFolder =
      this.folderModalMode === "edit"
        ? !!this.folderModalTarget?.parentFolderId
        : !!this.folderModalParent;

    return isSubFolder ? "Enter subfolder name" : "Enter folder name";
  }

  private resetFolderModalState() {
    this.folderNameValue = "";
    this.showLogoCropper = false;
    this.logoCropEvent = "";
    this.logoCropReady = false;
    this.lastLogoCrop = undefined;
    this.clearFolderLogo();
    this.folderLogoError = "";
    this.maxLengthExceeded = false;
    this.duplicateAlbumError = false;
    this.isSavingFolder = false;
  }

  isFolderModalOpen = false;

  private showFolderModal() {
    if (window.innerWidth <= 768) this.isAlbumMenuOpen = false;

    // A previous Bootstrap modal can leave its backdrop behind, which would
    // sit on top of this overlay and swallow every click.
    this.removeStrayModalBackdrops();

    this.isFolderModalOpen = true;
    this.cdr.detectChanges();
  }

  private removeStrayModalBackdrops() {
    const backdrops = document.getElementsByClassName("modal-backdrop");
    while (backdrops.length > 0) backdrops[0].remove();

    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }

  // ── FOLDER LOGO ─────────────────────────────────────────────────────────

  /**
   * Type and size are checked here as well as server-side, so a rejected file
   * produces a specific message instead of a bare 400.
   */
  onFolderLogoSelected(event: any) {
    const input = event?.target as HTMLInputElement;
    const file: File | undefined = input?.files?.[0];
    if (!file) return;

    if (!this.folderLogoTypes.includes(file.type)) {
      this.folderLogoError =
        "Folder logo must be an image (JPG, PNG, GIF or WEBP).";
      this.clearFolderLogo(input);
      return;
    }

    if (file.size > this.folderLogoMaxSize) {
      this.folderLogoError = "Folder logo must be 5 MB or smaller.";
      this.clearFolderLogo(input);
      return;
    }

    this.folderLogoError = "";

    // Straight into the cropper rather than using the raw file: logos render
    // in square tiles, so an uncropped landscape shot gets centre-cropped by
    // object-fit anyway, with no say over which part survives.
    this.openLogoCropper(event);
  }

  // ── LOGO CROPPER ────────────────────────────────────────────────────────

  showLogoCropper = false;
  logoCropEvent: any = "";
  logoCropReady = false;
  private lastLogoCrop?: ImageCroppedEvent;

  private openLogoCropper(event: any) {
    this.showLogoCropper = false;
    this.logoCropReady = false;
    this.lastLogoCrop = undefined;

    // Remount the cropper so re-picking the same file still reloads it.
    setTimeout(() => {
      this.logoCropEvent = event;
      this.showLogoCropper = true;
      this.cdr.detectChanges();
    }, 50);
  }

  onLogoCropped(event: ImageCroppedEvent) {
    if (event?.blob) {
      this.lastLogoCrop = event;
      this.logoCropReady = true;
    } else {
      this.logoCropReady = false;
    }
  }

  onLogoCropFailed() {
    this.showLogoCropper = false;
    this.logoCropEvent = "";
    this.folderLogoError = "That image could not be loaded. Try another file.";
    this.cdr.detectChanges();
  }

  applyLogoCrop() {
    const blob = this.lastLogoCrop?.blob;
    if (!this.logoCropReady || !blob) return;

    if (blob.size > this.folderLogoMaxSize) {
      this.folderLogoError = "Folder logo must be 5 MB or smaller.";
      return;
    }

    this.revokeLogoPreview();
    this.folderLogoFile = new File([blob], "folder-logo.png", {
      type: blob.type || "image/png",
      lastModified: Date.now(),
    });
    this.folderLogoPreview = URL.createObjectURL(blob);

    this.closeLogoCropper();
  }

  cancelLogoCrop() {
    this.closeLogoCropper();
  }

  private closeLogoCropper() {
    this.showLogoCropper = false;
    this.logoCropEvent = "";
    this.logoCropReady = false;
    this.lastLogoCrop = undefined;

    // Let the same file be picked again after a cancel.
    const input = document.getElementById(
      "folderLogoInput"
    ) as HTMLInputElement | null;
    if (input) input.value = "";

    this.cdr.detectChanges();
  }

  private revokeLogoPreview() {
    if (this.folderLogoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(this.folderLogoPreview);
    }
  }

  isRemovingLogo = false;

  /**
   * Clears the stored logo. There is a dedicated endpoint for this — a logo
   * cannot be removed by PATCHing an empty file.
   */
  removeFolderLogo() {
    const target = this.folderModalTarget;
    if (!target?._id || !target.folderLogo || this.isRemovingLogo) return;

    this.isRemovingLogo = true;
    this.vaultService.removeFolderLogo(target._id).subscribe({
      next: () => {
        this.isRemovingLogo = false;
        target.folderLogo = null;
        this.applyFolderPatch(target._id, { folderLogo: null });
        this.clearFolderLogo();
        alertModalWithoutConfirm("success", "Folder logo removed.");
      },
      error: (err) => {
        this.isRemovingLogo = false;
        this.handleFolderError(err, "Failed to remove the folder logo.");
      },
    });
  }

  /**
   * Drops the picked file only. In edit mode the folder keeps whatever logo it
   * already has — use removeFolderLogo() to clear the stored one.
   */
  private clearFolderLogo(input?: HTMLInputElement | null) {
    this.revokeLogoPreview();
    this.folderLogoFile = null;
    this.folderLogoPreview =
      this.folderModalMode === "edit"
        ? this.folderModalTarget?.folderLogo || null
        : null;
    if (input) input.value = "";
  }

  // ── FOLDER NAME VALIDATION ──────────────────────────────────────────────

  onAlbumNameInput(value: string) {
    this.folderNameValue = value ?? "";
    const trimmed = this.folderNameValue.trim();

    this.maxLengthExceeded = trimmed.length > 100;

    this.duplicateAlbumError =
      !!trimmed && trimmed.length <= 100 && this.isDuplicateFolderName(trimmed);
  }

  /**
   * Names are unique per parent, so this compares against the siblings at the
   * level being written to rather than every folder. When those siblings are
   * not loaded the server's 400 is the backstop.
   */
  private folderSiblings(): any[] {
    if (this.folderModalMode === "edit") {
      const id = this.folderModalTarget?._id;
      // Match by which list actually holds it: with three levels, "has a
      // parent" no longer implies its siblings are the loaded children.
      if (this.subFolders.some((f) => f._id === id)) return this.subFolders;
      if (this.allAlbums.some((f) => f._id === id)) return this.allAlbums;
      // Siblings not loaded (e.g. renaming the open folder) — the server's
      // uniqueness check is the backstop.
      return [];
    }

    return this.folderModalParent ? this.subFolders : this.allAlbums;
  }

  private isDuplicateFolderName(name: string): boolean {
    const target = name.toLowerCase();
    return this.folderSiblings().some(
      (f) =>
        f._id !== this.folderModalTarget?._id &&
        (f.folderName || "").toLowerCase() === target
    );
  }

  // ── SAVE ────────────────────────────────────────────────────────────────

  saveFolder() {
    if (this.isSavingFolder) return;

    const name = (this.folderNameValue || "").trim();
    const isEdit = this.folderModalMode === "edit";

    if (!isEdit && !name) {
      alertModalWithoutConfirm("warning", "Folder name is required");
      return;
    }

    if (name.length > 100) {
      this.maxLengthExceeded = true;
      return;
    }
    this.maxLengthExceeded = false;

    if (name && this.isDuplicateFolderName(name)) {
      this.duplicateAlbumError = true;
      return;
    }
    this.duplicateAlbumError = false;

    if (!isEdit) {
      this.submitFolderCreate(name);
      return;
    }

    const nameChanged =
      !!name && name !== (this.folderModalTarget?.folderName || "");

    // Both fields are optional server-side but at least one is required.
    if (!nameChanged && !this.folderLogoFile) {
      alertModalWithoutConfirm(
        "warning",
        "Nothing to update. Change the name or pick a new logo."
      );
      return;
    }

    this.submitFolderUpdate(nameChanged ? name : null);
  }

  private submitFolderCreate(name: string) {
    const parent = this.folderModalParent;
    const payload: any = { folderName: name };
    if (parent?._id) payload.parentFolderId = parent._id;

    this.isSavingFolder = true;
    this.vaultService.createVaultAlbum(payload, this.folderLogoFile).subscribe({
      next: () => {
        this.isSavingFolder = false;
        this.closeCreateModal();

        if (parent?._id) {
          parent.subFolderCount = (parent.subFolderCount || 0) + 1;
          this.applyFolderPatch(parent._id, {
            subFolderCount: parent.subFolderCount,
          });
          this.loadSubFolders(this.selectedAlbum || parent);
          alertModalWithoutConfirm("success", "Subfolder created successfully!");
        } else {
          this.getAllAlbums();
          alertModalWithoutConfirm("success", "Folder created successfully!");
        }
      },
      error: (err) => {
        this.isSavingFolder = false;
        this.handleFolderError(err, "Failed to create the folder. Please try again.");
      },
    });
  }

  private submitFolderUpdate(name: string | null) {
    const target = this.folderModalTarget;
    if (!target?._id) return;

    this.isSavingFolder = true;
    this.vaultService
      .updateVaultAlbum(target._id, name, this.folderLogoFile)
      .subscribe({
        next: (res: any) => {
          this.isSavingFolder = false;

          // The response echoes only what actually changed, so merge it in
          // rather than replacing the record.
          const data = res?.data || {};
          const patch: any = {};
          if (data.folderName || name) {
            patch.folderName = data.folderName || name;
          }
          if (data.folderLogo) patch.folderLogo = data.folderLogo;

          this.applyFolderPatch(target._id, patch);
          this.closeCreateModal();
          alertModalWithoutConfirm("success", "Folder updated successfully!");
        },
        error: (err) => {
          this.isSavingFolder = false;
          this.handleFolderError(
            err,
            "Failed to update the folder. Please try again."
          );
        },
      });
  }

  /** Merges changed folder fields into every list that can be showing it. */
  private applyFolderPatch(folderId: string, patch: any) {
    if (!patch || !Object.keys(patch).length) return;

    const apply = (list: any[]) => {
      const idx = list.findIndex((f) => f._id === folderId);
      if (idx !== -1) list[idx] = { ...list[idx], ...patch };
    };

    apply(this.allAlbums);
    apply(this.subFolders);
    apply(this.folderTrail);

    if (this.selectedAlbum?._id === folderId) {
      this.selectedAlbum = { ...this.selectedAlbum, ...patch };

      if (patch.folderName) {
        // Keep the deep link in step with the new name.
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { folder: folderId, folderName: patch.folderName },
          queryParamsHandling: "merge",
        });
      }
    }

    this.cdr.detectChanges();
  }

  /** The API puts its human-readable reason in `message` on every 400. */
  private apiMessage(err: any): string {
    return err?.error?.message || err?.message?.message || "";
  }

  private handleFolderError(err: any, fallback: string) {
    console.error("Vault folder error:", err);

    switch (this.apiMessage(err)) {
      case "Folder name already in use!":
        this.duplicateAlbumError = true;
        alertModalWithoutConfirm(
          "warning",
          "A folder with that name already exists here."
        );
        return;
      case "A folder can contain at most 20 subfolders!":
        alertModalWithoutConfirm(
          "warning",
          "This folder already has the maximum of 20 subfolders."
        );
        return;
      case "Maximum folder depth reached!":
        alertModalWithoutConfirm(
          "warning",
          `Folders can only be nested ${this.maxFolderDepth} levels deep.`
        );
        return;
      case "Nothing to update!":
        alertModalWithoutConfirm(
          "warning",
          "Nothing to update. Change the name or pick a new logo."
        );
        return;
      case "Folder logo must be an image!":
        alertModalWithoutConfirm(
          "warning",
          "Folder logo must be an image (JPG, PNG, GIF or WEBP)."
        );
        return;
      case "Maximum file size is 5242880":
        alertModalWithoutConfirm(
          "warning",
          "Folder logo must be 5 MB or smaller."
        );
        return;
      case "Folder does not have a logo!":
        // Already cleared elsewhere — reconcile instead of surfacing an error.
        if (this.folderModalTarget?._id) {
          this.folderModalTarget.folderLogo = null;
          this.applyFolderPatch(this.folderModalTarget._id, {
            folderLogo: null,
          });
          this.clearFolderLogo();
        }
        return;
      case "You Aren't Authorized to Update this Folder!":
        alertModalWithoutConfirm(
          "warning",
          "Only the folder's creator can update it."
        );
        return;
      case "Folder Not Found!":
        alertModalWithoutConfirm("warning", "That folder no longer exists.");
        return;
      default:
        alertModalWithoutConfirm("error", this.apiMessage(err) || fallback);
    }
  }

  closeCreateModal() {
    this.isFolderModalOpen = false;
    this.removeStrayModalBackdrops();

    this.folderModalMode = "create";
    this.folderModalParent = null;
    this.folderModalTarget = null;
    this.resetFolderModalState();
    this.cdr.detectChanges();
  }

  // GET ALL ALBUMS
  allAlbums: any[] = [];
  totalAlbums = 0;
  totalFolder = 0;
  isAlbumsLoading = false;

  /** Page size for the folder listing. */
  readonly albumPageSize = 200;

  /** Guards the auto-open so it only ever runs on the first listing. */
  private hasAutoOpenedFirstFolder = false;

  /**
   * Opens the first folder on entry so the pane is not empty. Runs once, and
   * never when something else already owns the selection.
   */
  private maybeAutoOpenFirstFolder() {
    if (this.hasAutoOpenedFirstFolder) return;

    // A shared link owns the selection — let it resolve instead.
    if (this.awaitingFolderId || this.route.snapshot.queryParams["folder"]) {
      return;
    }

    this.hasAutoOpenedFirstFolder = true;

    // Mobile opens on the folder grid, which this would skip past.
    if (this.isMobileView) return;
    // A search is the user narrowing the list, not asking to open anything.
    if (this.albumSearchQuery) return;
    if (this.selectedAlbum) return;

    const first = this.allAlbums[0];
    if (first) this.selectAlbum(first);
  }

  /**
   * Root folders only. The listing endpoint no longer returns every folder in
   * one flat list, so children are fetched separately by loadSubFolders().
   */
  getAllAlbums(page: number = 1, limit: number = 200, searchQuery?: string) {
    this.isAlbumsLoading = true;
    this.vaultService.getAllCreatedAlbums(page, limit, searchQuery).subscribe({
      next: (res: any) => {
        this.allAlbums = res?.data?.vaultFolders || [];
        this.totalAlbums = res?.data?.totalFolders || 0;
        this.totalFolder = res?.data?.totalCount;
        this.isAlbumsLoading = false;

        // A deep link that arrived before this listing can be resolved now.
        if (this.awaitingFolderId) {
          const pending = this.findLoadedFolder(this.awaitingFolderId);
          if (pending) {
            this.awaitingFolderId = null;
            this.applyOpenFolder(pending);
          }
        }

        this.maybeAutoOpenFirstFolder();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error("Error loading folders:", err);
        this.isAlbumsLoading = false;
      },
    });
  }

  // ── SUBFOLDER NAVIGATION ────────────────────────────────────────────────

  /** Breadcrumb, root first. Nesting is one level, so at most two entries. */
  folderTrail: any[] = [];
  /** Children of the trail's root folder: the strip, and the sibling list. */
  subFolders: any[] = [];
  isSubFoldersLoading = false;

  /** Server-side cap on direct children of a single parent. */
  readonly maxSubFolders = 20;

  /**
   * Id of the folder currently being opened. Opening involves awaited fetches,
   * so a slower earlier response must not overwrite a newer selection.
   */
  private pendingFolderId: string | null = null;

  /**
   * Opens a folder by id. A subfolder is never in `allAlbums` (that list is
   * roots only), so anything not already loaded comes from the detail endpoint.
   */
  /**
   * Deep link waiting on the root listing. A shared link (e.g. opened after
   * logging in) activates this route before getAllAlbums() has returned, so a
   * folder we cannot resolve yet must not clear the URL.
   */
  private awaitingFolderId: string | null = null;

  private async openFolderById(folderId: string) {
    this.pendingFolderId = folderId;

    const known = this.findLoadedFolder(folderId);
    if (known) {
      this.awaitingFolderId = null;
      this.applyOpenFolder(known);
      return;
    }

    const { folder, notFound } = await this.fetchFolder(folderId);

    if (this.pendingFolderId !== folderId) return;

    if (folder) {
      this.awaitingFolderId = null;
      this.applyOpenFolder(folder);
      return;
    }

    if (notFound) {
      // Genuinely gone — only now is dropping the deep link correct.
      this.awaitingFolderId = null;
      alertModalWithoutConfirm("warning", "That folder no longer exists.");
      this.goBackToFolders();
      return;
    }

    // The listing may still be in flight; retry from it when it lands rather
    // than throwing away the folder the link asked for.
    const fromList = this.findLoadedFolder(folderId);
    if (fromList) {
      this.awaitingFolderId = null;
      this.applyOpenFolder(fromList);
      return;
    }

    console.warn(
      `Vault: folder ${folderId} not resolved yet, waiting for the folder list.`
    );
    this.awaitingFolderId = folderId;
  }

  private findLoadedFolder(folderId: string): any | null {
    return (
      this.allAlbums.find((a) => a._id === folderId) ||
      this.subFolders.find((a) => a._id === folderId) ||
      this.folderTrail.find((f) => f._id === folderId) ||
      null
    );
  }

  private applyOpenFolder(folder: any) {
    this.resetAlbumState();
    this.searchQuery = "";
    this.selectedAlbum = folder;
    this.resetDateFilterState();
    this.vaultViewFilter = "all";
    this.clearMediaSelection();

    this.buildFolderTrail(folder);
    this.loadSubFolders(folder);
    this.getAllAlbumMedia(folder._id);
    this.cdr.detectChanges();
  }

  /**
   * `notFound` separates "the server says this folder is gone" from "we could
   * not resolve it right now" — only the former should discard a deep link.
   */
  private async fetchFolder(
    folderId: string
  ): Promise<{ folder: any | null; notFound: boolean }> {
    try {
      const res: any = await this.vaultService
        .getFolderById(folderId)
        .toPromise();

      return { folder: this.unwrapFolder(res), notFound: false };
    } catch (err) {
      const notFound = this.apiMessage(err) === "Folder Not Found!";
      console.error(`Could not load folder ${folderId}:`, err);
      return { folder: null, notFound };
    }
  }

  /** The detail payload's nesting varies, so try the shapes it can take. */
  private unwrapFolder(res: any): any | null {
    const data = res?.data ?? res;
    if (!data || typeof data !== "object") return null;

    const record =
      data.vaultFolder ??
      data.folder ??
      (Array.isArray(data.vaultFolders) ? data.vaultFolders[0] : null) ??
      data;

    if (!record || typeof record !== "object") return null;

    // Listings key the id as _id; some detail payloads use folderId.
    const id = record._id ?? record.folderId;
    if (!id) return null;

    return record._id ? record : { ...record, _id: id };
  }

  /**
   * Maintains the drill-down path. With three levels, parentFolderId alone no
   * longer identifies the trail from a level-3 folder, so the path is tracked
   * as the user navigates and only rebuilt from scratch on a cold start.
   */
  private async buildFolderTrail(folder: any) {
    if (!folder?.parentFolderId) {
      this.folderTrail = [folder];
      return;
    }

    // Drilled into a child of a folder already on the trail — extend it.
    const parentIdx = this.folderTrail.findIndex(
      (f) => f._id === folder.parentFolderId
    );
    if (parentIdx !== -1) {
      this.folderTrail = [
        ...this.folderTrail.slice(0, parentIdx + 1),
        folder,
      ];
      return;
    }

    // Jumped back to a crumb already on the trail — truncate to it.
    const selfIdx = this.folderTrail.findIndex((f) => f._id === folder._id);
    if (selfIdx !== -1) {
      this.folderTrail = this.folderTrail.slice(0, selfIdx + 1);
      return;
    }

    // Cold start (deep link or refresh): walk parentFolderId upwards.
    // Shown immediately so the header is never empty while ancestors load.
    this.folderTrail = [folder];

    const trail = [folder];
    let cursor = folder;

    // Bounded by the depth limit, so a bad parent chain cannot loop forever.
    for (let hop = 0; hop < this.maxFolderDepth; hop++) {
      if (!cursor?.parentFolderId) break;

      const parent =
        this.allAlbums.find((a) => a._id === cursor.parentFolderId) ||
        (await this.fetchFolder(cursor.parentFolderId)).folder;

      if (!parent) break;

      trail.unshift(parent);
      cursor = parent;
    }

    if (this.selectedAlbum?._id !== folder._id) return;

    this.folderTrail = trail;
    this.cdr.detectChanges();
  }

  /**
   * Loads the children of the trail's ROOT, not of the open folder. Inside a
   * subfolder that gives us its siblings, which the per-parent name-uniqueness
   * check needs; nesting is one level so a subfolder has no children anyway.
   */
  private loadSubFolders(folder: any) {
    const folderId = folder?._id;
    this.subFolders = [];

    // Used to load the trail root's children, which only worked while nesting
    // was one level deep. It is the open folder's own children now.
    if (!folderId || !this.canHaveSubFolders(folder)) return;

    const hasDateRange = !!this.startDate && !!this.endDate;

    this.isSubFoldersLoading = true;
    this.vaultService
      .getAllCreatedAlbums(
        1,
        this.maxSubFolders,
        undefined,
        folderId,
        true,
        hasDateRange ? this.startDate : undefined,
        hasDateRange ? this.endDate : undefined
      )
      .subscribe({
        next: (res: any) => {
          // Ignore a response for a folder the user has already navigated away
          // from, which would otherwise show the wrong children.
          if (this.selectedAlbum?._id !== folderId) return;

          this.subFolders = res?.data?.vaultFolders || [];
          this.isSubFoldersLoading = false;
          this.cdr.detectChanges();
          this.measureFoldersOffset();
        },
        error: (err) => {
          console.error("Error loading subfolders:", err);
          this.isSubFoldersLoading = false;
          this.measureFoldersOffset();
        },
      });
  }

  private clearSelectedFolder() {
    // Drop any in-flight deep link, so a late resolution cannot reopen a
    // folder the user has already navigated away from.
    this.awaitingFolderId = null;
    this.pendingFolderId = null;

    if (!this.selectedAlbum && !this.folderTrail.length) return;
    this.selectedAlbum = null;
    this.folderTrail = [];
    this.subFolders = [];
    this.resetAlbumState();
    this.cdr.detectChanges();
  }

  /** Deepest level a folder may live at. */
  readonly maxFolderDepth = 3;

  /**
   * 1, 2 or 3. The server sends `depth`; the fallbacks cover records from
   * before it existed — position in the current trail, else root vs child.
   */
  folderDepth(folder: any): number {
    if (typeof folder?.depth === "number") return folder.depth;

    const idx = this.folderTrail.findIndex((f) => f._id === folder?._id);
    if (idx !== -1) return idx + 1;

    return folder?.parentFolderId ? 2 : 1;
  }

  /** False only at the deepest level, where nothing may be nested below. */
  canHaveSubFolders(folder: any): boolean {
    return !!folder && this.folderDepth(folder) < this.maxFolderDepth;
  }

  /** True when the open folder has a parent to go back up to. */
  get canGoUpFolder(): boolean {
    return this.folderTrail.length > 1;
  }

  /** The folder one level up from the open one, if any. */
  get parentFolderInTrail(): any | null {
    return this.folderTrail.length > 1
      ? this.folderTrail[this.folderTrail.length - 2]
      : null;
  }

  /** Which of a folder's contents the right pane shows. */
  vaultViewFilter: "all" | "folders" | "media" | "featured" = "all";

  /** True only for JWTs carrying department "ADMIN". */
  isVaultAdmin = false;

  /** The `filter` value the media listing endpoint is called with. */
  get mediaApiFilter(): "all" | "featured" {
    return this.vaultViewFilter === "featured" ? "featured" : "all";
  }

  setVaultViewFilter(filter: "all" | "folders" | "media" | "featured") {
    const previousApiFilter = this.mediaApiFilter;
    this.vaultViewFilter = filter;

    // Featured is a server-side filter — the counts and paging come from the
    // same query — so switching in or out of it needs a fresh request.
    if (this.mediaApiFilter !== previousApiFilter && this.selectedAlbum?._id) {
      this.reloadMedia();
    }

    this.cdr.detectChanges();
    this.measureFoldersOffset();
  }

  private reloadMedia() {
    this.page = 1;
    this.groupedMedia = [];
    this.flatMediaList = [];
    this.totalMedia = 0;
    this.hasMoreData = true;
    this.lastMediaQuery = null;
    this.virtualGridData.set([]);
    this.clearMediaSelection();

    if (this.isFilteredView) {
      this.getAllAlbumMediaWithFilters();
    } else {
      this.getAllAlbumMedia(this.selectedAlbum._id, 1, this.limit, false);
    }
  }

  /**
   * On "all" the folders sit above the media, and the section is skipped
   * entirely when there is nothing to put in it.
   */
  get showFoldersSection(): boolean {
    // Only the deepest level can hold nothing below it.
    if (!this.canHaveSubFolders(this.selectedAlbum)) return false;
    if (this.vaultViewFilter === "media" || this.vaultViewFilter === "featured") {
      return false;
    }
    if (this.vaultViewFilter === "folders") return true;
    return this.subFolders.length > 0;
  }

  /** The Folders and Media tabs are pointless where folders cannot exist. */
  get showFolderTabs(): boolean {
    return this.canHaveSubFolders(this.selectedAlbum);
  }

  get showMediaSection(): boolean {
    return this.vaultViewFilter !== "folders";
  }

  /**
   * One spinner for the whole pane. Opening a folder fires the folder and
   * media requests together, which used to render a loader for each.
   */
  get isVaultContentLoading(): boolean {
    if (this.vaultViewFilter === "folders") return this.isSubFoldersLoading;

    if (
      !this.canHaveSubFolders(this.selectedAlbum) ||
      this.vaultViewFilter === "media" ||
      this.vaultViewFilter === "featured"
    ) {
      return this.isMediaLoading;
    }

    return this.isMediaLoading || this.isSubFoldersLoading;
  }

  canAddSubFolder(folder: any): boolean {
    return (
      this.canHaveSubFolders(folder) &&
      (folder.subFolderCount || 0) < this.maxSubFolders
    );
  }

  /** Files a folder holds directly. Subfolder contents never roll up. */
  folderItemCount(folder: any): number {
    const c = folder?.mediaCount;
    if (!c) return 0;
    return (
      (c.images || 0) +
      (c.videos || 0) +
      (c.documents || 0) +
      (c.audios || 0) +
      (c.others || 0)
    );
  }

  /**
   * Label for a folder card — subfolders and files together, e.g.
   * "1 folder · 9 items". mediaCount does not roll up from subfolders, so a
   * root that only holds subfolders reports zero files and would otherwise
   * read as empty.
   */
  folderCountLabel(folder: any): string {
    const subs = folder?.subFolderCount || 0;
    const items = this.folderItemCount(folder);

    const parts: string[] = [];
    if (subs > 0) parts.push(`${subs} ${subs === 1 ? "folder" : "folders"}`);
    if (items > 0 || !parts.length) {
      parts.push(`${items} ${items === 1 ? "item" : "items"}`);
    }

    return parts.join(" · ");
  }

  isFolderOwner(folder: any): boolean {
    return !!folder && folder.creatorId === this.uploaderID;
  }

  closeAlbumShareTooltip() {
    this.showAlbumShareTooltip = false;
    this.albumCopied = false;
  }

  // OPEN UPLOAD MODAL
  openUploadModal(album: any) {
    this.folderId = album._id;
    const modalName = document.getElementById("modalAlbumName");
    if (modalName) modalName.innerText = album.folderName;

    const modalElement = document.getElementById("uploadModal");
    if (modalElement) {
      const modalInstance = new (window as any).bootstrap.Modal(modalElement);
      modalInstance.show();
    } else {
      console.error("Modal element not found!");
    }
  }

  hideUploadModal() {
    this.closeUploadModal();
    this.clearFiles();
  }

  // SELECT ALBUM
  selectedAlbum: any = null;

  /** Navigates only; the queryParams handler is what actually opens it. */
  selectAlbum(album: any) {
    if (!album?._id || this.selectedAlbum?._id === album._id) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        folder: album._id,
        folderName: album.folderName,
      },
      queryParamsHandling: "merge",
    });
  }

  // DATE RANGE PICKER
  @ViewChild("dateRangeInput") dateRangeInput?: ElementRef<HTMLInputElement>;
  selectedDateRange: any = null;
  locale = {
    format: "MM/DD/YYYY",
    applyLabel: "OK",
    cancelLabel: "Cancel",
    customRangeLabel: "Custom Range",
    daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    firstDay: 1,
  };

  ranges: any = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  /**
   * Upper bound for the range picker — media cannot be uploaded in the future.
   *
   * Must be a string (or a dayjs object): ngx-daterangepicker-material is
   * dayjs-based and its maxDate setter stores null for any other type, so
   * handing it a moment would silently leave the calendar unbounded.
   *
   * Typed `any` because the *directive* declares this input as `Dayjs` while
   * the picker component it forwards to accepts `Dayjs | string`. The string
   * is handled correctly at runtime; only the declaration is too narrow.
   */
  get maxDate(): any {
    return moment().format("YYYY-MM-DD");
  }


  // Filter variables
  startDate: string = "";
  endDate: string = "";
  isMediaLoading = true;
  folderId: string = "";
  isFilteredView = false;
  displayDateRange: string | null = null;
  /**
   * Fires on the directive's `change`, which is also emitted when the picker
   * clears itself — with both keys null. That used to fall straight through
   * and leave isFilteredView set, so an empty folder rendered the "nothing
   * matches your filter" empty state rather than its own, and the toolbar
   * read as filtered with no range to show for it.
   */
  onDateSelected(event: any) {
    if (!event?.startDate || !event?.endDate) {
      this.resetDateFilterState();
      return;
    }

    const start = event.startDate;
    const end = event.endDate;

    this.isFilteredView = true;
    this.startDate = this.toLocalDateString(start);
    this.endDate = this.toLocalDateString(end);

    // Belt-and-braces: [maxDate] stops the calendar offering future days,
    // but a preset range can still resolve past today. Plain YYYY-MM-DD
    // strings compare correctly with <, so no parsing is needed.
    const today = this.toLocalDateString(new Date());
    if (this.startDate > today) this.startDate = today;
    if (this.endDate > today) this.endDate = today;

    this.displayDateRange = `${this.formatDisplayDate(
      start
    )} - ${this.formatDisplayDate(end)}`;

    this.getAllAlbumMediaWithFilters();
  }

  /**
   * The picked day as a plain YYYY-MM-DD calendar date.
   *
   * This used to send a full UTC timestamp with client-computed start/end of
   * day. The backend expands whatever it receives to IST day boundaries, so
   * the day logic ran twice in two timezones: an end of
   * 2026-08-30T23:59:59.999Z is already 05:29 on Aug 31 in IST, and the
   * backend's endOf('day') then stretched the range ~18 hours into the next
   * day. Sending the date alone lets the backend do that expansion once.
   *
   * Built from local date parts rather than toISOString(), which converts to
   * UTC and can shift the day backwards for users east of UTC.
   */
  toLocalDateString(momentDate: any): string {
    if (!momentDate) return "";

    const year = momentDate.year ? momentDate.year() : new Date(momentDate).getFullYear();
    const month = momentDate.month ? momentDate.month() : new Date(momentDate).getMonth();
    const date = momentDate.date ? momentDate.date() : new Date(momentDate).getDate();

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${year}-${pad(month + 1)}-${pad(date)}`;
  }

  onDatepickerHide() {
    console.log("Datepicker hidden");
  }
  formatDisplayDate(momentDate: any): string {
    const day = String(momentDate.date()).padStart(2, "0");
    const month = String(momentDate.month() + 1).padStart(2, "0");
    const year = momentDate.year();
    return `${day}/${month}/${year}`;
  }

  clearDateFilter() {
    this.resetDateFilterState();
    this.getAllAlbumMediaWithFilters();
  }

  /**
   * Drops the date filter and the text the picker is showing for it.
   *
   * The displayed text has to be cleared by hand because it is not driven by
   * the bound value. DaterangepickerComponent.clear() resets its *internal*
   * start/end to today rather than to null, and the directive's setValue()
   * falls back to that internal pair — via calculateChosenLabel() — whenever
   * the value it receives is truthy but missing a key, which is exactly the
   * `{startDate: null, endDate: null}` object it hands back on a clear. The
   * field then displayed a today-to-today range nobody had picked, and the
   * clear button is bound to selectedDateRange.startDate, so it was not even
   * rendered to get rid of it. Our own state is the source of truth here.
   */
  private resetDateFilterState() {
    this.selectedDateRange = null;
    this.isFilteredView = false;
    this.startDate = null;
    this.endDate = null;
    this.displayDateRange = null;

    const input = this.dateRangeInput?.nativeElement;
    if (input) {
      input.value = "";
    }
  }

  // GET ALL ALBUM MEDIA WITH FILTERS
  /**
   * Applies the date range. It used to run its own request at a hardcoded
   * limit of 500 and duplicate the response handling, so paging differed
   * between this view and every other one — and loadMore(), which pages
   * through getAllAlbumMedia(), dropped the date range on page 2.
   */
  getAllAlbumMediaWithFilters() {
    if (!this.selectedAlbum?._id && !this.folderId) {
      console.error("No album ID or folderId found to fetch media.");
      return;
    }

    const albumId = this.selectedAlbum?._id || this.folderId;

    // The date range and the search box are alternative views of a folder;
    // executeSearch() clears the dates, so this clears the query.
    this.searchQuery = "";
    this.lastMediaQuery = null;

    this.page = 1;
    this.groupedMedia = [];
    this.totalMedia = 0;
    this.hasMoreData = true;
    this.isScrollLoading = false;
    this.virtualGridData.set([]);
    this.clearMediaSelection();

    this.getAllAlbumMedia(albumId, 1, this.limit, false);

    // The folder listing takes the same range, so the folders shown above the
    // media stay consistent with it.
    if (this.selectedAlbum) this.loadSubFolders(this.selectedAlbum);
  }

  totalMedia = 0;
  groupedMedia: { month: string; items: any[] }[] = [];
  flatMediaList: any[] = [];
  page = 1;
  limit = 100;
  hasMoreData = true;
  isScrollLoading = false;


  private getItemsPerRow(): number {
    const containerWidth =
      this.scrollElement()?.nativeElement?.clientWidth ||
      (typeof window !== "undefined" ? window.innerWidth - 460 : 1200);
    const columns = Math.floor(containerWidth / 190);
    return Math.max(2, columns);
  }

  private updateVirtualGridData() {
    const newVirtualData: any[] = [];
    const itemsPerRow = this.getItemsPerRow();

    this.groupedMedia.forEach((group) => {
      if (group.items && group.items.length > 0) {
        newVirtualData.push({
          type: "header",
          month: group.month,
          size: 45,
        });

        for (let i = 0; i < group.items.length; i += itemsPerRow) {
          const rowItems = group.items.slice(i, i + itemsPerRow);
          newVirtualData.push({
            type: "media-row",
            month: group.month,
            items: rowItems,
            size: this.isMobileView ? 200 : 215,
            itemsPerRow: itemsPerRow,
          });
        }
      }
    });

    this.virtualGridData.set(newVirtualData);
    // Every media mutation funnels through here, so this is the one place the
    // cached selection counts need invalidating.
    this.markFeatureCountsDirty();
    this.virtualizer._willUpdate();
    this.cdr.detectChanges();
    this.measureFoldersOffset();
  }

  // VIRTUAL SCROLL HANDLER
  /** Coalesces the end-of-list check to one per frame. */
  private scrollCheckQueued = false;

  onVirtualScroll(event: any) {
    if (this.scrollCheckQueued) return;

    const element = event.target as HTMLElement;
    if (!element) return;

    this.scrollCheckQueued = true;

    // scrollHeight/clientHeight force a layout, so read them once per frame
    // after the browser has laid out rather than on every scroll event.
    requestAnimationFrame(() => {
      this.scrollCheckQueued = false;

      if (this.isMediaLoading || !this.hasMoreData || this.isScrollLoading) {
        return;
      }

      const scrollBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      if (scrollBottom < 400) {
        this.loadMore();
      }
    });
  }

  getAllAlbumMedia(id: string, page = 1, limit = this.limit, append = false) {
    this.isMediaLoading = page === 1;
    if (page > 1) {
      this.isScrollLoading = true;
    }

    const hasDateRange = !!this.startDate && !!this.endDate;

    this.vaultService
      // One request shape for every view — search, date range and the starred
      // filter all page with the same limit. The range is sent as a pair:
      // one date alone makes the backend skip date filtering entirely.
      .getVaultMedia(
        id,
        page,
        limit,
        (this.searchQuery || "").trim() || undefined,
        hasDateRange ? this.startDate : undefined,
        hasDateRange ? this.endDate : undefined,
        this.mediaApiFilter
      )
      .subscribe({
      next: (res: any) => {
        const mediaData = res?.data?.media || {};
        const incoming = Object.keys(mediaData)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
          .map((monthKey) => ({
            month: monthKey,
            items: mediaData[monthKey] || [],
          }));
        const totalItemsInResponse = incoming.reduce(
          (sum, group) => sum + group.items.length,
          0
        );
        const hasDataInResponse = totalItemsInResponse > 0;
        if (!hasDataInResponse && page > 1) {
          console.log("No more data available. Stopping pagination.");
          this.hasMoreData = false;
          this.isMediaLoading = false;
          this.isScrollLoading = false;
          return;
        }
        if (append) {
          for (const inc of incoming) {
            const idx = this.groupedMedia.findIndex(
              (g) => g.month === inc.month
            );
            if (idx > -1) {
              const existingIds = new Set(
                this.groupedMedia[idx].items.map((x) => x._id)
              );
              const filtered = inc.items.filter((x) => !existingIds.has(x._id));
              this.groupedMedia[idx].items = [
                ...this.groupedMedia[idx].items,
                ...filtered,
              ];
            } else {
              const insertAt = this.groupedMedia.findIndex(
                (g) =>
                  new Date(g.month).getTime() < new Date(inc.month).getTime()
              );
              if (insertAt === -1) this.groupedMedia.push(inc);
              else this.groupedMedia.splice(insertAt, 0, inc);
            }
          }
        } else {
          this.groupedMedia = incoming;
        }
        this.updateVirtualGridData();
        this.totalMedia =
          Number(res?.data?.mediaCount) ||
          this.groupedMedia.reduce((sum, group) => sum + group.items.length, 0);
        this.hasMoreData =
          this.groupedMedia.reduce(
            (sum, group) => sum + group.items.length,
            0
          ) < this.totalMedia;
        this.isMediaLoading = false;
        this.isScrollLoading = false;
      },
      error: (err) => {
        console.error("Error fetching media:", err);
        this.isMediaLoading = false;
        this.isScrollLoading = false;
        if (page > 1) {
          this.page--;
        }
      },
    });
  }

  // RESET ALBUM STATE
  private resetAlbumState() {
    // Queries are scoped to an album, so the dedupe guard must not carry the
    // previous album's query across a switch.
    this.lastMediaQuery = null;
    this.page = 1;
    this.groupedMedia = [];
    this.flatMediaList = [];
    this.totalMedia = 0;
    this.hasMoreData = true;
    this.isMediaLoading = false;
    this.isScrollLoading = false;
    this.virtualGridData.set([]);
    this.clearMediaSelection();
  }

  // LOAD MORE MEDIA
  loadMore() {
    if (this.isMediaLoading || !this.hasMoreData) return;

    this.page++;
    this.getAllAlbumMedia(this.selectedAlbum._id, this.page, this.limit, true);
  }

  trackByMediaId(index: number, item: any) {
    return item._id || index;
  }

  // GET PREVIEW URL
  getPreviewUrl(file: any): string {
    const ext = file.extension?.toLowerCase();

    const icons: Record<string, string> = {
      ".pdf": "assets/Archive_files/pdf.png",
      ".doc": "assets/docx-img.png",
      ".docx": "assets/docx-img.png",
      ".xls": "assets/excl-img.png",
      ".xlsx": "assets/excl-img.jpg",
      ".ppt": "assets/Archive_files/pptx.png",
      ".pptx": "assets/Archive_files/pptx.png",
      ".mp3": "assets/audio-file.png",
    };

    if ([".jpeg", ".jpg", ".png", ".gif", ".webp"].includes(ext)) {
      return file.fileUrl;
    }

    return icons[ext];
  }

  onImageError(event: any) {
    try {
      const el = event.target as HTMLImageElement;
      el.style.display = "none";
      const parent = el.parentElement;
      if (!parent) return;
      const fallback = document.createElement("div");
      fallback.className = "no-preview";
      fallback.style.cssText =
        "width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#eee;color:#666";
      fallback.innerText = "No preview";
      parent.appendChild(fallback);
    } catch (e) { }
  }

  isImage(file: any): boolean {
    if (!file) return false;
    let ext = file.extension?.toLowerCase();
    if (!ext && file.fileName) {
      ext = this.getFileExtension(file.fileName);
    }
    if (ext && !ext.startsWith('.')) {
      ext = '.' + ext;
    }
    if ([".jpeg", ".jpg", ".png", ".gif", ".webp"].includes(ext)) {
      return true;
    }
    if (file.fileType && typeof file.fileType === 'string' && file.fileType.startsWith('image/')) {
      return true;
    }
    return (!!file.compressedImgUrl || !!file.thumbnailUrl) && !this.isVideo(file);
  }

  isVideo(file: any): boolean {
    if (!file) return false;
    if (file.mediaType === "video") return true;
    let ext = file.extension?.toLowerCase();
    if (!ext && file.fileName) {
      ext = this.getFileExtension(file.fileName);
    }
    if (ext && !ext.startsWith('.')) {
      ext = '.' + ext;
    }
    if ([".mp4", ".mov", ".avi", ".mkv", ".webm"].includes(ext)) {
      return true;
    }
    if (file.fileType && typeof file.fileType === 'string' && file.fileType.startsWith('video/')) {
      return true;
    }
    return false;
  }

  isMediaVideo(file: any): boolean {
    return this.isVideo(file);
  }

  private videoDurationCache = new Map<string, string>();

  /** Probes already in flight, so each file is fetched at most once. */
  private videoDurationPending = new Set<string>();

  /**
   * Called from the template, so it runs on every change detection pass. It
   * used to build a fresh <video> and start a metadata download each time,
   * which during a scroll meant thousands of elements and requests.
   */
  getVideoDuration(file: any): string {
    const id = file?._id;
    if (!id) return "";

    const cached = this.videoDurationCache.get(id);
    if (cached !== undefined) return cached;

    if (file.duration) {
      const formatted = this.formatDuration(file.duration);
      this.videoDurationCache.set(id, formatted);
      return formatted;
    }

    if (!this.videoDurationPending.has(id) && file.fileUrl) {
      this.videoDurationPending.add(id);

      // Outside the zone: the probe's own events must not each trigger a
      // change detection pass of their own.
      this.zone.runOutsideAngular(() => {
        const video = document.createElement("video");
        video.preload = "metadata";

        const finish = (value: string) => {
          this.videoDurationPending.delete(id);
          this.videoDurationCache.set(id, value);
          video.removeAttribute("src");
          video.load();
          this.zone.run(() => this.cdr.detectChanges());
        };

        video.onloadedmetadata = () => finish(this.formatDuration(video.duration));
        // Cache the failure too, so a broken file is not retried forever.
        video.onerror = () => finish("");

        video.src = file.fileUrl;
      });
    }

    return "...";
  }

  trackByMonth(index: number, group: any) {
    return group.month;
  }

  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  uploadProgress: any = {};
  uploadingFiles: any[] = [];
  totalFiles = 0;
  uploadedCount = 0;
  overallProgress = 0;
  showUploadBox = false;
  currentFileName = "";
  uploadSuccess = false;
  isUploadPaused = false;
  uploadResumeToken: string = "";

  selectedFiles: any[] = [];
  currentFileProgress: number = 0;
  isUploading: boolean = false;

  async uploadVaultFiles() {
    console.log("clicked");

    if (!this.selectedFiles.length) {
      alertModalWithoutConfirm("warning", "No files selected.");
      return;
    }

    const invalidFiles = this.selectedFiles.filter((f) => !this.isValidMediaFile(f));
    if (invalidFiles.length > 0) {
      alertModalWithoutConfirm(
        "error",
        "Unsupported file format. Please upload a supported image or video file."
      );
      this.clearFiles();
      return;
    }

    const files: File[] = this.selectedFiles;
    this.totalFiles = files.length;
    this.uploadedCount = 0;
    this.overallProgress = 0;
    this.currentFileProgress = 0;
    this.showUploadBox = true;
    this.uploadSuccess = false;
    this.isUploadPaused = false;
    this.uploadProgress = {};
    this.isUploading = true;
    this.uploadSessions.clear();
    this.uploadResumeToken = this.generateResumeToken();
    this.saveUploadState(true);

    try {
      await this.processFilesUpload(files);
      if (this.uploadSuccess) {
        alertModalWithoutConfirm("success", "All files uploaded successfully!");
        setTimeout(() => {
          this.closeUploadBox();
        }, 1000);
      }
    } catch (err: any) {
      console.error("Upload failed:", err);
      for (const s of this.uploadSessions.values()) {
        if (s.mediaId) this.removeMediaItem(s.mediaId);
        if (s.tempId) this.removePlaceholder(s.tempId);
      }
      const errMsg = this.extractErrorMessage(err, "File upload failed.");
      alertModalWithoutConfirm(
        "error",
        errMsg
      );
    } finally {
      this.isUploading = false;
      if (!this.isUploadPaused) {
        this.closeUploadModal();
      }
    }
  }

  private extractErrorMessage(err: any, defaultMsg: string = "File upload failed."): string {
    if (!err) return defaultMsg;
    if (typeof err === "string") return err;

    const payload = err.error || err;
    if (typeof payload === "string") return payload;

    if (payload.message) {
      if (typeof payload.message === "string") return payload.message;
      if (typeof payload.message === "object") {
        if (typeof payload.message.message === "string") return payload.message.message;
        if (typeof payload.message.error === "string") return payload.message.error;
        if (Array.isArray(payload.message.lowResolutionFiles) && payload.message.lowResolutionFiles.length > 0) {
          return "Images must be at least 1280x720 (HD)";
        }
      }
    }

    if (payload.data) {
      if (typeof payload.data === "string") return payload.data;
      if (typeof payload.data === "object") {
        if (typeof payload.data.message === "string") return payload.data.message;
        if (typeof payload.data.error === "string") return payload.data.error;
        if (Array.isArray(payload.data.lowResolutionFiles) && payload.data.lowResolutionFiles.length > 0) {
          return "Images must be at least 1280x720 (HD)";
        }
      }
    }

    if (Array.isArray(payload.lowResolutionFiles) && payload.lowResolutionFiles.length > 0) {
      return "Images must be at least 1280x720 (HD)";
    }

    if (typeof err.message === "string") return err.message;

    return defaultMsg;
  }

  /**
   * Identity of a file across pause/resume. Name alone is not enough because
   * two picked files can share a name.
   */
  private fileKey(file: File): string {
    return `${file.name}::${file.size}::${(file as any).lastModified ?? 0}`;
  }

  private async processFilesUpload(files: File[]) {
    // A File's bytes cannot survive serialization, so anything that is not a
    // real File (e.g. a plain object rehydrated from localStorage) cannot be
    // read back and must not be treated as uploadable.
    if (!files.length || files.some((f) => !(f instanceof File))) {
      throw new Error(
        "The selected files are no longer available in this tab. Please choose them again."
      );
    }

    const invalidFiles = files.filter((f) => !this.isValidMediaFile(f));
    if (invalidFiles.length > 0) {
      throw new Error(
        "Unsupported file format. Please upload a supported image or video file."
      );
    }

    // Only files without a live multipart session need pre-signed URLs. On a
    // resume the existing sessions are reused so the already-uploaded parts
    // stay valid under the same uploadId.
    const needSession = files.filter(
      (f) => !this.uploadSessions.has(this.fileKey(f))
    );

    if (needSession.length) {
      const presignedResp: any = await this.vaultService
        .vaultPreSignedUrls(
          needSession.map((file) => ({
            fileName: file.name,
            totalChunks: Math.ceil(file.size / CHUNK_SIZE),
            fileSize: file.size,
          })),
          this.folderId
        )
        .toPromise();

      const presigned = presignedResp?.data || [];
      if (presigned.length !== needSession.length) {
        throw new Error("Invalid pre-signed URL response from the server.");
      }

      needSession.forEach((file, i) => {
        const data = presigned[i];
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        this.uploadSessions.set(this.fileKey(file), {
          file,
          uploadId: data.uploadId,
          uniqueFileName: data.uniqueFileName,
          mediaId: data.mediaId,
          urls: data.urls,
          totalChunks,
          parts: new Array(totalChunks).fill(null),
          nextChunk: 0,
          tempId: null,
          percent: 0,
          completed: false,
        });
      });
    }

    for (const file of files) {
      if (this.isUploadPaused) break;

      const session = this.uploadSessions.get(this.fileKey(file));
      if (!session || session.completed) continue;

      if (!session.tempId) {
        session.tempId = this.addProcessingPlaceholder(file);
      }

      this.currentFileName = file.name;
      this.applyFileProgress(session, session.percent);
      this.saveUploadState(true);

      try {
        await this.uploadFileInChunks(session);
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        if (session.tempId) this.removePlaceholder(session.tempId);
        // The multipart upload is unusable now; drop it so a later retry
        // starts a clean one instead of appending to a broken part list.
        this.uploadSessions.delete(this.fileKey(file));
        continue;
      }

      // uploadFileInChunks returns early on pause, leaving the session intact.
      if (this.isUploadPaused) break;

      session.completed = true;
      this.applyFileProgress(session, 100);

      if (session.tempId) {
        this.replacePlaceholderWithMediaId(session.tempId, session.mediaId, {
          fileName: file.name,
        });
        session.tempId = null;
      }

      this.uploadedCount++;
      this.saveUploadState(true);
    }

    if (this.isUploadPaused) {
      this.saveUploadState(true);
      return;
    }

    const finished = [...this.uploadSessions.values()].filter(
      (s) => s.completed
    );
    if (!finished.length) {
      throw new Error("No files could be uploaded. Please try again.");
    }

    const completeRes: any = await this.vaultService
      .completeUpload(
        finished.map((s) => ({
          uniqueFileName: s.uniqueFileName,
          uploadId: s.uploadId,
          mediaId: s.mediaId,
          parts: s.parts.filter(Boolean),
        })),
        this.folderId
      )
      .toPromise();

    const lowResList: string[] =
      completeRes?.data?.lowResolutionFiles ||
      completeRes?.lowResolutionFiles ||
      completeRes?.message?.lowResolutionFiles ||
      [];
    const lowResMsg = this.extractErrorMessage(
      completeRes,
      "Images must be at least 1280x720 (HD)"
    );

    const hasLowRes =
      lowResList.length > 0 ||
      (typeof completeRes?.message === "string" && completeRes.message.toLowerCase().includes("1280x720")) ||
      (typeof completeRes?.message?.message === "string" && completeRes.message.message.toLowerCase().includes("1280x720")) ||
      (typeof completeRes?.data?.message === "string" && completeRes.data.message.toLowerCase().includes("1280x720"));

    if (hasLowRes) {
      const successfulSessions = finished.filter((s) => {
        if (
          lowResList.includes(s.uniqueFileName) ||
          lowResList.includes(s.file?.name)
        ) {
          return false;
        }
        if (completeRes?.data?.files && Array.isArray(completeRes.data.files)) {
          return completeRes.data.files.some(
            (f: any) =>
              f?.mediaId === s.mediaId ||
              f?.uniqueFileName === s.uniqueFileName ||
              f?.fileName === s.file?.name
          );
        }
        return false;
      });

      const failedSessions = finished.filter(
        (s) => !successfulSessions.includes(s)
      );
      for (const failed of failedSessions) {
        if (failed.mediaId) this.removeMediaItem(failed.mediaId);
        if (failed.tempId) this.removePlaceholder(failed.tempId);
      }

      if (successfulSessions.length === 0) {
        this.uploadSuccess = false;
        this.uploadSessions.clear();
        this.saveUploadState(true);
        this.clearFiles();
        throw new Error(lowResMsg);
      }

      this.uploadSuccess = true;
      this.updateAlbumMediaCountOnce(successfulSessions.length);

      await Promise.all(
        successfulSessions.map((s) => this.mergeServerMediaRecord(s.mediaId))
      );

      this.uploadSessions.clear();
      this.saveUploadState(true);
      this.clearFiles();

      setTimeout(() => {
        this.clearUploadState();
      }, 3000);

      alertModalWithoutConfirm("warning", lowResMsg);
      return;
    }

    this.uploadSuccess = true;
    this.updateAlbumMediaCountOnce(finished.length);

    // Reconcile each optimistic tile with the canonical server record, so a
    // just-uploaded item behaves the same as one loaded by a page refresh
    // (fileUrl for download/playback, and any field the client cannot derive).
    await Promise.all(
      finished.map((s) => this.mergeServerMediaRecord(s.mediaId))
    );

    this.uploadSessions.clear();
    this.saveUploadState(true);
    this.clearFiles();

    setTimeout(() => {
      this.clearUploadState();
    }, 3000);
  }

  private removePlaceholder(tempId: string) {
    for (const group of this.groupedMedia) {
      const idx = group.items.findIndex((i) => i._id === tempId);
      if (idx !== -1) {
        group.items.splice(idx, 1);
        break;
      }
    }
    this.placeholderMap.delete(tempId);
    this.updateVirtualGridData();
  }

  private removeMediaItem(id: string) {
    if (!id) return;
    for (const group of this.groupedMedia) {
      const idx = group.items.findIndex((i: any) => i._id === id);
      if (idx !== -1) {
        group.items.splice(idx, 1);
        break;
      }
    }
    const mediaIdx = this.allMedia.findIndex((m) => m._id === id);
    if (mediaIdx !== -1) {
      this.allMedia.splice(mediaIdx, 1);
    }
    this.placeholderMap.delete(id);
    this.groupedMedia = this.groupedMedia.filter((g) => g.items.length > 0);
    this.updateVirtualGridData();
    this.cdr.detectChanges();
  }

  /**
   * Pulls the authoritative record for a just-uploaded media item and merges it
   * into the grid and the fullscreen list.
   */
  private async mergeServerMediaRecord(mediaId: string) {
    try {
      const res: any = await this.vaultService
        .getMediaById(mediaId)
        .toPromise();

      const record = res?.data?.media ?? res?.data ?? res;
      if (!record?._id) return;

      this.applyMediaRecord(record);
    } catch (error) {
      // The placeholder already carries name/extension/size, so a failed
      // reconcile only costs the server-side URLs until the next refetch.
      console.error(`Could not refresh vault media ${mediaId}:`, error);
    }
  }

  private applyMediaRecord(record: any) {
    // `isProcessing` is owned by the socket handler, so it is never taken from
    // the record: the "processing finished" event may already have arrived.
    for (const group of this.groupedMedia) {
      const idx = group.items.findIndex((i) => i._id === record._id);
      if (idx !== -1) {
        group.items[idx] = {
          ...group.items[idx],
          ...record,
          isProcessing: group.items[idx].isProcessing,
        };
        break;
      }
    }

    const mediaIdx = this.allMedia.findIndex((m) => m._id === record._id);
    if (mediaIdx !== -1) {
      this.allMedia[mediaIdx] = {
        ...this.allMedia[mediaIdx],
        ...record,
        isProcessing: this.allMedia[mediaIdx].isProcessing,
      };
    }

    if (this.selectedMedia?._id === record._id) {
      this.selectedMedia = {
        ...this.selectedMedia,
        ...record,
        isProcessing: this.selectedMedia.isProcessing,
      };
    }

    this.updateVirtualGridData();
    this.cdr.detectChanges();
  }

  /** Returns the dotted, lowercased extension the type helpers expect (".jpg"). */
  private getFileExtension(fileName: string): string {
    const dot = fileName.lastIndexOf(".");
    return dot > -1 ? fileName.slice(dot).toLowerCase() : "";
  }

  private addProcessingPlaceholder(file: File) {
    const monthKey = new Date().toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const tempId =
      "temp-" + Date.now() + "-" + Math.random().toString(36).substr(2, 6);

    // isImage()/isVideo()/getPreviewUrl() all branch on `extension`, and the
    // size label reads `size`. Without them a freshly uploaded item renders as
    // the generic "0 KB" file card until the album is refetched.
    const placeholder = {
      _id: tempId,
      fileName: file.name,
      extension: this.getFileExtension(file.name),
      size: file.size,
      uploadedByName: localStorage.getItem("guestName") || "You",
      uploadedById: localStorage.getItem("guestID"),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isProcessing: true,
      mediaType: file.type?.startsWith("video") ? "video" : "image",
    };

    const existingGroup = this.groupedMedia.find((g) => g.month === monthKey);
    if (existingGroup) existingGroup.items.unshift(placeholder);
    else this.groupedMedia.unshift({ month: monthKey, items: [placeholder] });

    this.placeholderMap.set(tempId, { monthKey, tempId });

    this.updateVirtualGridData();
    return tempId;
  }

  private replacePlaceholderWithMediaId(
    tempId: string,
    mediaId: string,
    extra?: any
  ) {
    for (const group of this.groupedMedia) {
      const idx = group.items.findIndex((i) => i._id === tempId);
      if (idx !== -1) {
        const item = group.items[idx];
        item._id = mediaId;
        item.isProcessing = true;
        if (extra?.fileName) item.fileName = extra.fileName;
        this.placeholderMap.delete(tempId);
        break;
      }
    }
    this.updateVirtualGridData();
  }

  /**
   * Uploads the parts this session still owes, continuing from
   * `session.nextChunk`. Returns early (without throwing) when paused, so the
   * caller can leave the session in place for a later resume.
   */
  private async uploadFileInChunks(session: VaultUploadSession): Promise<void> {
    const { file, urls, totalChunks } = session;

    for (let i = session.nextChunk; i < totalChunks; i++) {
      if (this.isUploadPaused) return;

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const presignedUrl = urls?.[i]?.url;

      if (!presignedUrl) {
        throw new Error(
          `Missing pre-signed URL for part ${i + 1} of "${file.name}".`
        );
      }

      let response: any;
      try {
        response = await this.uploadWithRetry(presignedUrl, chunk, 3, (loaded) => {
          // Real byte-level progress within the current chunk.
          const chunkFraction = chunk.size ? loaded / chunk.size : 1;
          const percent = Math.min(
            99,
            Math.round(((i + chunkFraction) / totalChunks) * 100)
          );
          this.applyFileProgress(session, percent);
        });
      } catch (error: any) {
        throw new Error(
          `Failed to upload part ${i + 1} of "${file.name}": ${error?.message || error}`
        );
      }

      const rawEtag =
        response?.headers?.etag ??
        response?.headers?.["etag"] ??
        response?.data?.ETag ??
        response?.headers?.["x-amz-etag"];
      const etag = rawEtag ? String(rawEtag).replace(/"/g, "") : "";

      // A placeholder ETag would be accepted here and then rejected by S3 at
      // assembly time, so fail now with the actual cause.
      if (!etag) {
        throw new Error(
          `S3 returned no ETag for part ${i + 1} of "${file.name}". ` +
            `The bucket CORS policy must expose it ` +
            `(Access-Control-Expose-Headers: ETag).`
        );
      }

      session.parts[i] = { PartNumber: i + 1, ETag: etag };
      session.nextChunk = i + 1;

      this.applyFileProgress(
        session,
        Math.round(((i + 1) / totalChunks) * 100)
      );
      this.saveUploadState();
    }
  }

  private applyFileProgress(session: VaultUploadSession, percent: number) {
    session.percent = percent;
    this.uploadProgress[session.file.name] = percent;
    this.currentFileProgress = percent;
    this.recalculateOverallProgress();
  }

  closeUploadModal() {
    const modal = document.getElementById("uploadModal");
    if (modal) {
      modal.classList.remove("show");
      modal.classList.remove("fade");
      modal.style.display = "none";

      const backdrops = document.getElementsByClassName("modal-backdrop");
      while (backdrops.length > 0) {
        backdrops[0].remove();
      }

      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      const fileInput = document.querySelector(
        '#uploadModal input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      this.selectedFiles = [];
      this.filePreviews = [];
    }
  }

  private updateAlbumMediaCountOnce(newFilesCount: number) {
    if (!this.selectedAlbum || !newFilesCount) return;

    this.totalMedia = (this.totalMedia || 0) + newFilesCount;

    const album = this.selectedAlbum;

    album.mediaCount = {
      images: album.mediaCount?.images || 0,
      videos: album.mediaCount?.videos || 0,
      documents: album.mediaCount?.documents || 0,
      audios: album.mediaCount?.audios || 0,
      others:
        (album.mediaCount?.others || 0) + newFilesCount, // Add once only
    };

    album.updatedAt = new Date();

    // The open folder may be a subfolder, which lives in subFolders rather
    // than in the root listing.
    for (const list of [this.allAlbums, this.subFolders, this.folderTrail]) {
      const idx = list.findIndex((a) => a._id === album._id);
      if (idx !== -1) list[idx] = { ...album };
    }

    this.selectedAlbum = { ...album };


  }


  isDragOver = false;
  preventGlobalDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const items = event.dataTransfer?.items;
    const files: File[] = [];

    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    } else if (event.dataTransfer?.files) {
      // fallback for browsers without .items support
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        files.push(event.dataTransfer.files[i]);
      }
    }

    if (files.length > 0) {
      this.addDroppedFiles(files);
    }

    if (event.dataTransfer) {
      event.dataTransfer.clearData();
    }
  }

  addDroppedFiles(newFiles: File[]) {
    const validFiles: File[] = [];
    let hasInvalid = false;

    for (const file of newFiles) {
      if (this.isValidMediaFile(file)) {
        validFiles.push(file);
      } else {
        hasInvalid = true;
      }
    }

    if (hasInvalid) {
      alertModalWithoutConfirm(
        "error",
        "Unsupported file format. Please upload a supported image or video file."
      );
    }

    if (validFiles.length > 0) {
      this.selectedFiles = [...this.selectedFiles, ...validFiles];

      this.filePreviews = this.selectedFiles.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      }));
    }
  }

  private async uploadWithRetry(
    url: string,
    chunk: Blob,
    maxRetries: number,
    onProgress?: (loaded: number) => void
  ): Promise<any> {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.put(url, chunk, {
          headers: {
            "Content-Type": "application/octet-stream",
          },
          timeout: 30000,
          onUploadProgress: (event) => onProgress?.(event.loaded ?? 0),
        });

        return response;
      } catch (error) {
        lastError = error;
        console.warn(`Upload attempt ${attempt + 1} failed:`, error);

        if (attempt < maxRetries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, attempt))
          );
        }
      }
    }

    throw lastError;
  }

  /** True only while the picked File objects are still held in memory. */
  private canResumeInMemory(): boolean {
    return (
      this.selectedFiles.length > 0 &&
      this.selectedFiles.every((f) => f instanceof File)
    );
  }

  resumeUpload() {
    if (!this.showUploadBox || this.uploadSuccess || this.isUploading) return;

    if (!this.canResumeInMemory()) {
      alertModalWithoutConfirm(
        "warning",
        "This upload can no longer be resumed. Please select the files again."
      );
      this.cancelUpload();
      return;
    }

    this.isUploadPaused = false;
    this.isUploading = true;

    // Resume by session state, not by index: a file that failed earlier must
    // not be skipped just because later files finished.
    const remainingFiles = this.selectedFiles.filter((file: File) => {
      const session = this.uploadSessions.get(this.fileKey(file));
      return !session || !session.completed;
    });

    this.processFilesUpload(remainingFiles)
      .then(() => {
        if (this.uploadSuccess) {
          alertModalWithoutConfirm(
            "success",
            "All files uploaded successfully!"
          );
          setTimeout(() => this.closeUploadBox(), 1000);
        }
      })
      .catch((err: any) => {
        console.error("Resume failed:", err);
        for (const s of this.uploadSessions.values()) {
          if (s.mediaId) this.removeMediaItem(s.mediaId);
          if (s.tempId) this.removePlaceholder(s.tempId);
        }
        const errMsg = this.extractErrorMessage(err, "File upload failed.");
        alertModalWithoutConfirm("error", errMsg);
      })
      .finally(() => {
        this.isUploading = false;
      });
  }

  // Pause upload functionality
  pauseUpload() {
    if (
      this.showUploadBox &&
      !this.uploadSuccess &&
      this.overallProgress < 100
    ) {
      this.isUploadPaused = true;
      this.saveUploadState(true);
    }
  }

  // Cancel upload functionality
  cancelUpload() {
    this.isUploadPaused = true;
    this.isUploading = false;
    this.showUploadBox = false;
    this.uploadSuccess = false;
    this.overallProgress = 0;
    this.currentFileProgress = 0;
    this.uploadProgress = {};
    this.uploadSessions.clear();
    this.clearFiles();
    this.clearUploadState();
  }

  // Generate unique resume token
  private generateResumeToken(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  clearFiles() {
    this.selectedFiles = [];
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  }

  closeUploadBox() {
    this.showUploadBox = false;
    this.uploadSuccess = false;
    this.overallProgress = 0;
    this.currentFileProgress = 0;
    this.uploadSessions.clear();
    this.clearFiles();
    this.clearUploadState();
  }

  filePreviews: any[] = [];
  onFilesSelected(event: any | { target?: { files?: FileList } }) {
    const files: FileList | undefined =
      event?.target?.files || (event as FileList);

    if (!files || files.length === 0) return;

    const newFiles: File[] = Array.from(files);
    const validFiles: File[] = [];
    let hasInvalid = false;

    for (const file of newFiles) {
      if (this.isValidMediaFile(file)) {
        validFiles.push(file);
      } else {
        hasInvalid = true;
      }
    }

    if (hasInvalid) {
      alertModalWithoutConfirm(
        "error",
        "Unsupported file format. Please upload a supported image or video file."
      );
      const fileInput = document.querySelector(
        '#uploadModal input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }

    if (validFiles.length > 0) {
      this.selectedFiles = [...this.selectedFiles, ...validFiles];

      this.filePreviews = this.selectedFiles.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      }));
    }
  }

  /** Guards against a second delete of the same folder, which now 400s. */
  private deletingFolderIds = new Set<string>();

  async deleteAlbum(folder: any, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();

    const target =
      typeof folder === "string"
        ? this.allAlbums.find((a) => a._id === folder) ||
          this.subFolders.find((a) => a._id === folder) || { _id: folder }
        : folder;

    const id = target?._id;
    if (!id || this.deletingFolderIds.has(id)) return;

    const subs = target.subFolderCount || 0;
    const confirmed = await alertModalDelete(
      "Are you sure you want to delete this album?",
      subs > 0
        ? `This will also delete its ${subs} ${
            subs === 1 ? "subfolder" : "subfolders"
          }, anything nested inside them, and every file they hold. You won't be able to revert this!`
        : undefined
    );
    if (!confirmed || this.deletingFolderIds.has(id)) return;

    this.deletingFolderIds.add(id);

    this.vaultService.deleteVaultAlbum(id).subscribe({
      next: () => {
        // Was window.location.reload(), which threw away any in-flight upload.
        this.deletingFolderIds.delete(id);
        this.afterFolderDeleted(target);
        alertModalWithoutConfirm("success", "Album deleted successfully!");
      },
      error: (err) => {
        this.deletingFolderIds.delete(id);

        // Deleting an already-deleted folder is a 400 now, not a silent
        // success — treat it as gone rather than surfacing an error.
        if (this.apiMessage(err) === "Folder Not Found!") {
          this.afterFolderDeleted(target);
          return;
        }

        console.error("Error deleting album:", err);
        alertModalWithoutConfirm(
          "error",
          "Failed to delete the album. Please try again."
        );
      },
    });
  }

  /** Deletion cascades, so drop the folder from every list that held it. */
  private afterFolderDeleted(folder: any) {
    const id = folder._id;
    const parentId = folder.parentFolderId;

    this.subFolders = this.subFolders.filter((f) => f._id !== id);

    if (parentId) {
      const parent =
        this.allAlbums.find((a) => a._id === parentId) ||
        this.folderTrail.find((f) => f._id === parentId);
      if (parent) {
        parent.subFolderCount = Math.max(0, (parent.subFolderCount || 0) - 1);
      }
    }

    const wasOpen = this.selectedAlbum?._id === id;
    const ancestorDeleted = this.folderTrail.some((f) => f._id === id);

    if (wasOpen || ancestorDeleted) {
      // Step back to the parent when it survived, otherwise to the root list.
      const survivingParent = wasOpen
        ? this.folderTrail.find((f) => f._id === parentId)
        : null;

      if (survivingParent) this.selectAlbum(survivingParent);
      else this.goBackToFolders();
    }

    this.getAllAlbums();
  }

  /**
   * Persists the *display* state of an upload. File contents are never stored
   * (they cannot be serialized), so this snapshot can restore the UI but never
   * the bytes. Throttled to at most one write per second unless forced,
   * because it used to run on every chunk.
   */
  saveUploadState(force = false) {
    const now = Date.now();
    if (!force && now - this.lastStateWrite < 1000) return;
    this.lastStateWrite = now;

    const uploadState = {
      pendingFileNames: this.selectedFiles
        .map((f) => f?.name)
        .filter(Boolean),
      uploadProgress: this.uploadProgress,
      overallProgress: this.overallProgress,
      uploadedCount: this.uploadedCount,
      totalFiles: this.totalFiles,
      currentFileName: this.currentFileName,
      folderId: this.folderId,
      showUploadBox: this.showUploadBox,
      uploadSuccess: this.uploadSuccess,
      isUploadPaused: this.isUploadPaused,
      uploadResumeToken: this.uploadResumeToken,
      timestamp: now,
    };
    localStorage.setItem("vaultUploadState", JSON.stringify(uploadState));
  }

  restoreUploadState() {
    const saved = localStorage.getItem("vaultUploadState");
    if (!saved) return;

    let state: any;
    try {
      state = JSON.parse(saved);
    } catch (error) {
      console.error("Corrupt vault upload state, discarding:", error);
      this.clearUploadState();
      return;
    }

    const isRecent = state?.timestamp && Date.now() - state.timestamp < 3600000;
    if (!isRecent) {
      this.clearUploadState();
      return;
    }

    this.uploadProgress = state.uploadProgress || {};
    this.overallProgress = state.overallProgress || 0;
    this.uploadedCount = state.uploadedCount || 0;
    this.totalFiles = state.totalFiles || 0;
    this.currentFileName = state.currentFileName || "";
    this.folderId = state.folderId || this.folderId;
    this.showUploadBox = state.showUploadBox || false;
    this.uploadSuccess = state.uploadSuccess || false;
    this.isUploadPaused = state.isUploadPaused || false;
    this.uploadResumeToken = state.uploadResumeToken || "";

    // The File objects live only in memory. If they are gone the upload cannot
    // be continued, so tear the box down instead of showing a progress bar
    // that can never move.
    if (this.showUploadBox && !this.uploadSuccess && !this.canResumeInMemory()) {
      this.resetUploadStateForRefresh();
    }
  }

  clearUploadState() {
    localStorage.removeItem("vaultUploadState");
    this.isUploadPaused = false;
    this.uploadResumeToken = "";
    this.lastStateWrite = 0;
  }

  async deleteMediaFile(
    event: MouseEvent,
    mediaId: string,
    fileName: string
  ): Promise<void> {
    event.stopPropagation(); // stop immediately

    const confirmed = await alertModalDelete(
      `Are you sure you want to delete "${fileName}"?`
    );
    if (!confirmed) return;

    try {
      this.vaultService.deleteSingleMedia(mediaId).subscribe({
        next: () => {
          // Single delete is a hard failure for non-owners, so reaching here
          // means the file really is gone.
          this.removeMediaFromGrid([mediaId]);
        },
        error: (err) => {
          console.error("Error deleting media:", err);

          if (this.apiMessage(err) === "Media Not Found!") {
            this.removeMediaFromGrid([mediaId]);
            return;
          }

          alertModalWithoutConfirm(
            "error",
            this.apiMessage(err) ||
              "Failed to delete the media. Please try again."
          );
        },
      });
    } catch (error) {
      console.error("Error in deleteMediaFile:", error);
      alertModalWithoutConfirm(
        "error",
        "An error occurred while deleting the media."
      );
    }
  }

  /** Drops files from every list that can be showing them, and re-lays out. */
  private removeMediaFromGrid(mediaIds: string[]) {
    if (!mediaIds?.length) return;

    const gone = new Set(mediaIds);
    let removed = 0;

    for (const group of this.groupedMedia) {
      const before = group.items.length;
      group.items = group.items.filter((item) => !gone.has(item._id));
      removed += before - group.items.length;
    }

    this.groupedMedia = this.groupedMedia.filter((g) => g.items.length > 0);
    this.allMedia = this.allMedia.filter((m) => !gone.has(m._id));
    this.totalMedia = Math.max(0, this.totalMedia - removed);

    if (this.selectedMedia && gone.has(this.selectedMedia._id)) {
      this.closeFullScreen();
    }

    mediaIds.forEach((id) => this.selectedMediaIds.delete(id));

    this.updateVirtualGridData();
    this.cdr.detectChanges();
  }

  isMediaOwner(file: any): boolean {
    return !!file && file.uploadedById === this.uploaderID;
  }

  // ── FEATURED MEDIA (admin-curated) ──────────────────────────────────────

  /** In-flight feature toggles, so a double click cannot race itself. */
  private featurePendingIds = new Set<string>();

  isFeaturePending(file: any): boolean {
    return this.featurePendingIds.has(file?._id);
  }

  /**
   * Featuring is a single shared flag set by admins, not a per-user favourite,
   * so the badge reads the same for everyone. Both calls are idempotent.
   */
  toggleFeatured(file: any, event?: MouseEvent) {
    event?.stopPropagation();

    if (!this.isVaultAdmin || !file || this.isPlaceholderMedia(file)) return;

    const mediaId = file._id;
    if (this.featurePendingIds.has(mediaId)) return;

    const shouldFeature = !file.isFeatured;
    this.featurePendingIds.add(mediaId);

    const request = shouldFeature
      ? this.vaultService.featureMedia(mediaId)
      : this.vaultService.unfeatureMedia(mediaId);

    request.subscribe({
      next: (res: any) => {
        this.featurePendingIds.delete(mediaId);

        const data = res?.data || {};
        this.applyMediaPatch(mediaId, {
          isFeatured: data.isFeatured ?? shouldFeature,
          featuredById: data.featuredById ?? null,
          featuredAt: data.featuredAt ?? null,
        });

        alertToast(
          "success",
          shouldFeature ? "Starred for everyone" : "Star removed"
        );

        // The Featured tab is a server-side listing, so an unfeatured file no
        // longer belongs in it.
        if (this.vaultViewFilter === "featured" && !shouldFeature) {
          this.removeMediaFromGrid([mediaId]);
        }
      },
      error: (err) => {
        this.featurePendingIds.delete(mediaId);
        console.error("Error updating featured state:", err);

        const message = this.apiMessage(err);
        if (message === "Only an admin can feature media!") {
          this.isVaultAdmin = false;
          alertModalWithoutConfirm(
            "warning",
            "Only an admin can feature media."
          );
          return;
        }

        if (message === "Media Not Found!") {
          this.removeMediaFromGrid([mediaId]);
          alertModalWithoutConfirm("warning", "That file no longer exists.");
          return;
        }

        alertModalWithoutConfirm(
          "error",
          message || "Failed to update the featured state."
        );
      },
    });
  }

  /** Optimistic tiles have no server id yet, so they cannot be acted on. */
  private isPlaceholderMedia(file: any): boolean {
    return !file?._id || String(file._id).startsWith("temp-");
  }

  // ── RENAME MEDIA ────────────────────────────────────────────────────────

  renameMediaTarget: any = null;
  renameMediaBaseName = "";
  renameMediaError = "";
  isRenamingMedia = false;

  /** Shown as static text: the server always keeps the original extension. */
  get renameMediaExtension(): string {
    return this.renameMediaTarget?.extension || "";
  }

  openRenameMediaModal(file: any, event?: MouseEvent) {
    event?.stopPropagation();
    if (!file || this.isPlaceholderMedia(file) || !this.isMediaOwner(file)) {
      return;
    }

    this.renameMediaTarget = file;
    this.renameMediaBaseName = this.baseFileName(file);
    this.renameMediaError = "";
    this.isRenamingMedia = false;
  }

  closeRenameMediaModal() {
    this.renameMediaTarget = null;
    this.renameMediaBaseName = "";
    this.renameMediaError = "";
    this.isRenamingMedia = false;
  }

  /** File name minus its extension — only the base name is ever sent. */
  private baseFileName(file: any): string {
    const name: string = file?.fileName || "";
    const ext: string = file?.extension || "";

    if (ext && name.toLowerCase().endsWith(ext.toLowerCase())) {
      return name.slice(0, name.length - ext.length);
    }

    const dot = name.lastIndexOf(".");
    return dot > 0 ? name.slice(0, dot) : name;
  }

  saveMediaRename() {
    if (this.isRenamingMedia) return;

    const target = this.renameMediaTarget;
    const name = (this.renameMediaBaseName || "").trim();

    if (!name) {
      this.renameMediaError = "File name cannot be empty.";
      return;
    }

    if (name.length > 255) {
      this.renameMediaError = "File name cannot be more than 255 characters.";
      return;
    }

    if (name === this.baseFileName(target)) {
      this.closeRenameMediaModal();
      return;
    }

    this.renameMediaError = "";
    this.isRenamingMedia = true;

    this.vaultService.renameMedia(target._id, name).subscribe({
      next: (res: any) => {
        this.isRenamingMedia = false;

        // Use the stored name rather than assuming what we sent survived.
        const stored = res?.data?.fileName || `${name}${target.extension || ""}`;
        this.applyMediaPatch(target._id, { fileName: stored });
        this.closeRenameMediaModal();
      },
      error: (err) => {
        this.isRenamingMedia = false;
        console.error("Error renaming media:", err);

        switch (this.apiMessage(err)) {
          case "File Name cannot be empty!":
            this.renameMediaError = "File name cannot be empty.";
            return;
          case "You Aren't Authorized to Rename this Media!":
            this.renameMediaError =
              "Only the person who uploaded this file can rename it.";
            return;
          case "Media Not Found!":
            this.closeRenameMediaModal();
            this.removeMediaFromGrid([target._id]);
            alertModalWithoutConfirm(
              "warning",
              "That file no longer exists."
            );
            return;
          default:
            this.renameMediaError =
              this.apiMessage(err) || "Failed to rename the file.";
        }
      },
    });
  }

  /** Merges changed media fields into every list that can be showing them. */
  private applyMediaPatch(mediaId: string, patch: any) {
    if (!patch || !Object.keys(patch).length) return;

    for (const group of this.groupedMedia) {
      const idx = group.items.findIndex((i) => i._id === mediaId);
      if (idx !== -1) {
        group.items[idx] = { ...group.items[idx], ...patch };
        break;
      }
    }

    const mediaIdx = this.allMedia.findIndex((m) => m._id === mediaId);
    if (mediaIdx !== -1) {
      this.allMedia[mediaIdx] = { ...this.allMedia[mediaIdx], ...patch };
    }

    if (this.selectedMedia?._id === mediaId) {
      this.selectedMedia = { ...this.selectedMedia, ...patch };
    }

    this.updateVirtualGridData();
    this.cdr.detectChanges();
  }

  // ── MULTI-SELECT + BULK DELETE ──────────────────────────────────────────

  selectedMediaIds = new Set<string>();
  /** Anchor for shift-click range selection. */
  private lastSelectedMediaId: string | null = null;
  isBulkDeleting = false;

  get selectedMediaCount(): number {
    return this.selectedMediaIds.size;
  }

  /**
   * A click inside a tile. The thumbnail opens the viewer; anywhere else on
   * the card toggles selection.
   */
  openMediaFromCard(file: any, event: MouseEvent) {
    event.stopPropagation();

    const target = event.target as HTMLElement | null;
    const onThumbnail = !!target?.closest(
      "img, video, .video-thumb-wrapper, .video-overlay"
    );

    if (!onThumbnail) {
      this.toggleMediaSelection(file, event);
      return;
    }

    if (this.isPlaceholderMedia(file) || file.isProcessing) return;

    this.openFullScreen(file);
  }

  clearMediaSelection() {
    this.selectedMediaIds.clear();
    this.lastSelectedMediaId = null;
    this.markFeatureCountsDirty();
  }

  isMediaSelected(file: any): boolean {
    return this.selectedMediaIds.has(file?._id);
  }

  /** Grid order, so a shift-click can select everything between two tiles. */
  private flatMediaIds(): string[] {
    return this.groupedMedia.flatMap((g) => g.items.map((i) => i._id));
  }

  toggleMediaSelection(file: any, event?: MouseEvent) {
    event?.stopPropagation();

    if (this.isPlaceholderMedia(file) || file.isProcessing) return;

    const id = file._id;
    const shiftPressed = !!event?.shiftKey;

    if (shiftPressed && this.lastSelectedMediaId) {
      const flat = this.flatMediaIds();
      const from = flat.indexOf(this.lastSelectedMediaId);
      const to = flat.indexOf(id);

      if (from !== -1 && to !== -1) {
        const [start, end] = from < to ? [from, to] : [to, from];
        for (let i = start; i <= end; i++) this.selectedMediaIds.add(flat[i]);
        this.lastSelectedMediaId = id;
        this.markFeatureCountsDirty();
        this.cdr.detectChanges();
        return;
      }
    }

    if (this.selectedMediaIds.has(id)) this.selectedMediaIds.delete(id);
    else this.selectedMediaIds.add(id);

    this.lastSelectedMediaId = id;
    this.markFeatureCountsDirty();
    this.cdr.detectChanges();
  }

  selectAllLoadedMedia() {
    for (const group of this.groupedMedia) {
      for (const item of group.items) {
        if (!this.isPlaceholderMedia(item) && !item.isProcessing) {
          this.selectedMediaIds.add(item._id);
        }
      }
    }
    this.markFeatureCountsDirty();
    this.cdr.detectChanges();
  }

  /**
   * Bulk delete is partial-success: files the user does not own and files that
   * no longer exist are skipped while the rest are still deleted, and a 2xx can
   * come back with deletedCount 0. Only the ids the server reports as deleted
   * are removed from the grid.
   */
  async bulkDeleteSelected() {
    const ids = [...this.selectedMediaIds];
    if (!ids.length || this.isBulkDeleting) return;

    const confirmed = await alertModalDelete(
      `Delete ${ids.length} selected ${ids.length === 1 ? "file" : "files"}?`,
      "Files you didn't upload will be skipped. You won't be able to revert this!"
    );
    if (!confirmed) return;

    this.isBulkDeleting = true;

    const deletedIds: string[] = [];
    const skipped: any[] = [];
    let requestFailed = false;

    try {
      // The endpoint caps a request at 100 ids, so larger selections chunk.
      for (let i = 0; i < ids.length; i += BULK_DELETE_CHUNK_SIZE) {
        const chunk = ids.slice(i, i + BULK_DELETE_CHUNK_SIZE);
        const res: any = await this.vaultService
          .bulkDeleteMedia(chunk)
          .toPromise();

        const data = res?.data || {};
        deletedIds.push(...(data.deletedIds || []));
        skipped.push(...(data.skipped || []));
      }
    } catch (err) {
      requestFailed = true;
      console.error("Bulk delete failed:", err);
    } finally {
      this.isBulkDeleting = false;
    }

    // Whatever the server confirmed is gone, even if a later chunk threw.
    this.removeMediaFromGrid(deletedIds);
    this.lastSelectedMediaId = null;

    if (requestFailed && !deletedIds.length) {
      alertModalWithoutConfirm(
        "error",
        "Failed to delete the selected files. Please try again."
      );
      return;
    }

    this.reportBulkDeleteResult(deletedIds.length, skipped);
  }

  // ── BULK FEATURE / UNFEATURE (admin) ────────────────────────────────────

  isBulkFeaturing = false;

  /**
   * Selected ids that are not already in the target state. Both endpoints are
   * idempotent, but sending only what actually changes keeps updatedCount
   * meaningful — otherwise unfeaturing a selection of 10 with 2 featured would
   * report "10 unfeatured".
   */
  private selectionIdsToChange(isFeatured: boolean): string[] {
    const ids: string[] = [];

    for (const group of this.groupedMedia) {
      for (const item of group.items) {
        if (!this.selectedMediaIds.has(item._id)) continue;
        if (!!item.isFeatured === isFeatured) continue;
        ids.push(item._id);
      }
    }

    return ids;
  }

  /**
   * Cached because the template reads these several times per change
   * detection pass, and a full scan of the loaded media on every one of those
   * is what made scrolling stutter. Invalidated whenever the selection or the
   * featured flags change.
   */
  private featureCounts = { featured: 0, unfeatured: 0 };
  private featureCountsDirty = true;

  private markFeatureCountsDirty() {
    this.featureCountsDirty = true;
  }

  private ensureFeatureCounts() {
    if (!this.featureCountsDirty) return;

    let featured = 0;
    let unfeatured = 0;

    if (this.selectedMediaIds.size) {
      for (const group of this.groupedMedia) {
        for (const item of group.items) {
          if (!this.selectedMediaIds.has(item._id)) continue;
          if (item.isFeatured) featured++;
          else unfeatured++;
        }
      }
    }

    this.featureCounts = { featured, unfeatured };
    this.featureCountsDirty = false;
  }

  /** Selected files that already carry the featured flag. */
  get selectedFeaturedCount(): number {
    this.ensureFeatureCounts();
    return this.featureCounts.featured;
  }

  /** Selected files that are not featured yet. */
  get selectedUnfeaturedCount(): number {
    this.ensureFeatureCounts();
    return this.featureCounts.unfeatured;
  }

  /**
   * Features or unfeatures the whole selection. Partial success is normal —
   * only the ids the server reports as updated are applied locally.
   *
   * Permissions are checked per request rather than per item, so a non-admin
   * changes nothing and gets a single 400; the controls are hidden for them.
   */
  async bulkSetFeatured(isFeatured: boolean) {
    if (!this.isVaultAdmin || this.isBulkFeaturing) return;

    // Nothing in the selection would change, so there is no call to make.
    const ids = this.selectionIdsToChange(isFeatured);
    if (!ids.length) return;

    this.isBulkFeaturing = true;

    const updatedIds: string[] = [];
    const skipped: any[] = [];
    let failureMessage = "";

    try {
      // The endpoint caps a request at 100 ids, so larger selections chunk.
      for (let i = 0; i < ids.length; i += BULK_FEATURE_CHUNK_SIZE) {
        const chunk = ids.slice(i, i + BULK_FEATURE_CHUNK_SIZE);
        const res: any = await this.vaultService
          .bulkSetFeatured(chunk, isFeatured)
          .toPromise();

        const data = res?.data || {};
        updatedIds.push(...(data.updatedIds || []));
        skipped.push(...(data.skipped || []));
      }
    } catch (err) {
      failureMessage = this.apiMessage(err);
      console.error("Bulk feature update failed:", err);
    } finally {
      this.isBulkFeaturing = false;
    }

    // Whatever the server confirmed, even if a later chunk threw.
    this.applyFeaturedPatch(updatedIds, isFeatured);

    // On the Featured tab an unfeatured file no longer belongs in the list.
    if (this.vaultViewFilter === "featured" && !isFeatured) {
      this.removeMediaFromGrid(updatedIds);
    }

    if (failureMessage && !updatedIds.length) {
      if (failureMessage === "Only an admin can feature media!") {
        this.isVaultAdmin = false;
        alertModalWithoutConfirm(
          "warning",
          "Only an admin can feature media."
        );
        return;
      }

      alertModalWithoutConfirm(
        "error",
        failureMessage || "Failed to update the selected files."
      );
      return;
    }

    this.reportBulkFeatureResult(updatedIds.length, skipped, isFeatured);
  }

  /**
   * Applies the flag to many items in one pass. featuredById/featuredAt are
   * stamped optimistically from the acting admin and the local clock — the
   * bulk response returns only the ids it changed, not each new record.
   */
  private applyFeaturedPatch(mediaIds: string[], isFeatured: boolean) {
    if (!mediaIds.length) return;

    const target = new Set(mediaIds);
    const patch = {
      isFeatured,
      featuredById: isFeatured ? this.uploaderID : null,
      featuredAt: isFeatured ? new Date().toISOString() : null,
    };

    for (const group of this.groupedMedia) {
      group.items = group.items.map((item) =>
        target.has(item._id) ? { ...item, ...patch } : item
      );
    }

    this.allMedia = this.allMedia.map((item) =>
      target.has(item._id) ? { ...item, ...patch } : item
    );

    if (this.selectedMedia && target.has(this.selectedMedia._id)) {
      this.selectedMedia = { ...this.selectedMedia, ...patch };
    }

    this.updateVirtualGridData();
    this.cdr.detectChanges();
  }

  private reportBulkFeatureResult(
    updatedCount: number,
    skipped: any[],
    isFeatured: boolean
  ) {
    const action = isFeatured ? "featured" : "unfeatured";
    const parts = [
      `${updatedCount} ${updatedCount === 1 ? "file" : "files"} ${action}`,
    ];
    if (skipped.length) parts.push(`${skipped.length} skipped`);

    const reasons = new Set(skipped.map((s) => s?.reason));
    const detail = reasons.has("Media Not Found!")
      ? " — some files no longer exist or are still uploading."
      : ".";

    alertModalWithoutConfirm(
      updatedCount === 0 ? "warning" : skipped.length ? "warning" : "success",
      parts.join(", ") + detail
    );
  }

  private reportBulkDeleteResult(deletedCount: number, skipped: any[]) {
    const parts = [
      `${deletedCount} ${deletedCount === 1 ? "file" : "files"} deleted`,
    ];
    if (skipped.length) parts.push(`${skipped.length} skipped`);

    const reasons = new Set(skipped.map((s) => s?.reason));
    const details: string[] = [];
    if (reasons.has("You Aren't Authorized to Delete this Media!")) {
      details.push("you can only delete files you uploaded");
    }
    if (reasons.has("Media Not Found!")) {
      details.push("some files no longer exist");
    }

    const message =
      parts.join(", ") + (details.length ? ` — ${details.join("; ")}.` : ".");

    alertModalWithoutConfirm(
      deletedCount === 0 ? "warning" : skipped.length ? "warning" : "success",
      message
    );
  }

  selectedMedia: any = null;
  swiperInstance: any;

  openFullScreen(selectedFile: any, mediaList?: any[]) {
    this.selectedMedia = selectedFile;

    if (mediaList && mediaList.length > 0) {
      this.allMedia = mediaList;
    } else if (this.groupedMedia?.length) {
      this.allMedia = this.groupedMedia.flatMap((group) => group.items);
    }

    let initialIndex = this.allMedia.findIndex(
      (f) =>
        (f._id && selectedFile._id && f._id === selectedFile._id) ||
        (f.mediaId && selectedFile.mediaId && f.mediaId === selectedFile.mediaId) ||
        (f.fileUrl && selectedFile.fileUrl && f.fileUrl === selectedFile.fileUrl)
    );

    if (initialIndex === -1) {
      initialIndex = 0;
    }

    if (this.allMedia[initialIndex]) {
      this.selectedMedia = this.allMedia[initialIndex];
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }

      const swiperEl = document.getElementById("fullscreenSwiper");
      if (!swiperEl) return;

      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1,
        centeredSlides: true,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        keyboard: { enabled: true },
        initialSlide: initialIndex,
        on: {
          init: () => {
            swiperEl.classList.add("ready");
            if (this.swiperInstance && this.swiperInstance.activeIndex !== initialIndex) {
              this.swiperInstance.slideTo(initialIndex, 0);
            }
          },
          slideChange: (swiper) => {
            this.selectedMedia = this.allMedia[swiper.activeIndex];
            this.cdr.detectChanges();
          },
        },
      });

      if (this.swiperInstance && this.swiperInstance.activeIndex !== initialIndex) {
        this.swiperInstance.slideTo(initialIndex, 0);
      }
    }, 50);
  }

  closeFullScreen() {
    this.selectedMedia = null;
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    if (this.groupedMedia?.length) {
      this.allMedia = this.groupedMedia.flatMap((group) => group.items);
    }
  }

  downloadMedia(file?: any) {
    const currentFile = file || this.selectedMedia;
    if (!currentFile) return;
    const link = document.createElement("a");
    link.href = currentFile.fileUrl || currentFile.url || currentFile.compressedImgUrl;
    link.download = currentFile.fileName;
    link.click();
  }

  formatFileSize(size: number): string {
    if (!size) return "0 KB";
    const i = Math.floor(Math.log(size) / Math.log(1024));
    return (
      (size / Math.pow(1024, i)).toFixed(2) + " " + ["B", "KB", "MB", "GB"][i]
    );
  }

  // ALBUM SHARE
  showAlbumShareTooltip = false;
  albumShareableLink = "";
  albumCopied = false;

  toggleAlbumShareTooltip() {
    this.showAlbumShareTooltip = !this.showAlbumShareTooltip;

    if (this.showAlbumShareTooltip && !this.albumShareableLink) {
      const folderId = this.selectedAlbum._id;
      const folderName = encodeURIComponent(this.selectedAlbum.folderName);

      // folderName, not foldername: the component reads and rewrites that
      // casing, so the lowercase form left both keys in the URL.
      this.albumShareableLink = `${environment.redirectUrl}#/vault?folder=${folderId}&folderName=${folderName}`;
    }
  }

  copyAlbumLink(input: HTMLInputElement) {
    input.select();
    document.execCommand("copy");
    this.albumCopied = true;
    setTimeout(() => (this.albumCopied = false), 2000);
  }

  // SINGLE MEDIA SHARE
  showMediaShareTooltip = false;
  mediaShareableLink = "";
  mediaCopied = false;

  toggleMediaShareTooltip() {
    this.showMediaShareTooltip = !this.showMediaShareTooltip;

    if (
      this.showMediaShareTooltip &&
      !this.mediaShareableLink &&
      this.selectedMedia
    ) {
      // Generate media-specific share link
      this.mediaShareableLink = `${environment.redirectUrl}#/vault/media/${this.selectedMedia._id}`;
    }
  }

  copyMediaLink(input: HTMLInputElement) {
    input.select();
    document.execCommand("copy");
    this.mediaCopied = true;
    setTimeout(() => (this.mediaCopied = false), 2000);
  }
  isAlbumMenuOpen = false;

  toggleAlbumMenu() {
    this.isAlbumMenuOpen = !this.isAlbumMenuOpen;
  }

  closeAlbumMenu() {
    this.isAlbumMenuOpen = false;
  }

  handleCreateButtonClick() {
    this.openCreateFolderModal(null);
  }

  searchQuery: string = '';
  /** Keystrokes in the media search box; debounced in ngOnInit. */
  private mediaSearch$ = new Subject<string>();
  /** Last media query actually sent; see lastAlbumQuery. Reset per album. */
  private lastMediaQuery: string | null = null;

  onSearchMedia(value: string) {
    this.searchQuery = value ?? '';
    this.mediaSearch$.next(this.searchQuery.trim());
  }

  /** Immediate search: Enter key and the search icon bypass the debounce. */
  applyMediaSearch() {
    this.executeSearch(this.searchQuery.trim());
  }

  private executeSearch(query: string) {
    if (!this.selectedAlbum?._id) return;
    if (query === this.lastMediaQuery) return;
    this.lastMediaQuery = query;

    // searchQuery is not reassigned here: the input owns it, and writing the
    // trimmed value back mid-typing moved the caret.

    // A search is a fresh listing, so the date filter and paging both reset.
    this.resetDateFilterState();

    this.page = 1;
    this.groupedMedia = [];
    this.totalMedia = 0;
    this.hasMoreData = true;
    this.isScrollLoading = false;
    this.virtualGridData.set([]);
    this.clearMediaSelection();

    // Always this.limit. Passing the current total sent an arbitrary page
    // size, and loadMore() then paged with a different one — so page 2
    // overlapped page 1 and scrolling for more results did nothing useful.
    this.getAllAlbumMedia(this.selectedAlbum._id, 1, this.limit, false);
  }

  clearSearch() {
    // ngModel owns the input's value; the old code reached into the DOM with
    // querySelector('.search-input'), which matched the *album* search box in
    // the sidebar (same class, earlier in the document).
    this.searchQuery = '';
    this.executeSearch('');
  }

  // Album Search
  albumSearchQuery: string = '';

  /** Keystrokes; debounced in ngOnInit so typing alone triggers the search. */
  private albumSearch$ = new Subject<string>();
  /** Last query actually sent, so a debounced tick and an Enter press that
   *  resolve to the same text do not fire two identical requests. */
  private lastAlbumQuery: string | null = null;

  onAlbumSearchChange(value: string) {
    this.albumSearchQuery = value ?? '';
    this.albumSearch$.next(this.albumSearchQuery.trim());
  }

  /** Immediate search: Enter key and the search icon bypass the debounce. */
  applyAlbumSearch() {
    this.runAlbumSearch(this.albumSearchQuery.trim());
  }

  clearAlbumSearch() {
    this.albumSearchQuery = '';
    this.runAlbumSearch('');
  }

  private runAlbumSearch(query: string) {
    if (query === this.lastAlbumQuery) return;
    this.lastAlbumQuery = query;

    // Searching only ever refreshes the root list; the open folder and its
    // media are untouched. searchQuery does not reach into subfolders.
    // Fixed page size — the folder count is not a page size.
    this.getAllAlbums(1, this.albumPageSize, query || undefined);
  }

  // ─── Advanced Multimodal Search ──────────────────────────────────────────
  showAdvancedSearch: boolean = false;
  advSearchType: 'text' | 'image' = 'text';
  advTextQuery: string = '';
  advSelectedImageFile: File | null = null;
  advSelectedImagePreview: string | null = null;
  advStartDate: string = '';
  advEndDate: string = '';
  isAdvSearching: boolean = false;
  isLoadingMoreAdv: boolean = false;
  hasPerformedAdvSearch: boolean = false;
  advSearchResults: any[] = [];
  advTotalCount: number = 0;
  advNextCursor: string | null = null;
  advSearchError: string = '';

  @ViewChild('advFileInput') advFileInputElement?: ElementRef<HTMLInputElement>;

  toggleAdvancedSearch() {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  closeAdvancedSearch() {
    this.showAdvancedSearch = false;
  }

  setAdvSearchType(type: 'text' | 'image') {
    this.advSearchType = type;
  }

  onAdvImageSelected(event: any) {
    const file = event?.target?.files?.[0];
    if (file) {
      this.handleAdvImageFile(file);
    }
  }

  onImageDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onImageDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      this.handleAdvImageFile(file);
    }
  }

  private handleAdvImageFile(file: File) {
    this.advSelectedImageFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.advSelectedImagePreview = e.target.result;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  onDropzoneClick(fileInput: HTMLInputElement) {
    if (!this.advSelectedImagePreview) {
      fileInput.click();
    }
  }

  clearAdvImage(event?: Event, fileInput?: HTMLInputElement) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.advSelectedImageFile = null;
    this.advSelectedImagePreview = null;
    if (fileInput) {
      fileInput.value = '';
    }
    if (this.advFileInputElement?.nativeElement) {
      this.advFileInputElement.nativeElement.value = '';
    }
  }

  performAdvancedSearch(loadMore: boolean = false) {
    if (this.isAdvSearching || this.isLoadingMoreAdv) return;

    const cursorToPass = loadMore ? (this.advNextCursor || undefined) : undefined;

    if (this.advSearchType === 'text') {
      const q = this.advTextQuery.trim();
      if (!q) {
        alertToast('Please enter search text', 'warning');
        return;
      }
      if (loadMore) {
        this.isLoadingMoreAdv = true;
      } else {
        this.isAdvSearching = true;
        this.hasPerformedAdvSearch = true;
        this.advSearchResults = [];
        this.advTotalCount = 0;
        this.advNextCursor = null;
      }
      this.advSearchError = '';

      this.vaultService.searchVaultMediaByText(
        q,
        20,
        cursorToPass,
        this.advStartDate || undefined,
        this.advEndDate || undefined
      ).subscribe({
        next: (res: any) => {
          this.isAdvSearching = false;
          this.isLoadingMoreAdv = false;
          const rawData = res?.data;
          const nextCursorVal = rawData?.nextCursor ?? res?.nextCursor ?? rawData?.next_cursor ?? res?.next_cursor ?? rawData?.cursor ?? res?.cursor ?? rawData?.pagination?.nextCursor ?? res?.pagination?.nextCursor ?? null;
          this.advNextCursor = (nextCursorVal && nextCursorVal !== 'null' && nextCursorVal !== 'undefined') ? String(nextCursorVal) : null;
          if (rawData?.totalCount !== undefined && rawData?.totalCount !== null) {
            this.advTotalCount = rawData.totalCount;
          } else if (res?.totalCount !== undefined && res?.totalCount !== null) {
            this.advTotalCount = res.totalCount;
          }
          const list = rawData?.media || res?.media || rawData || (Array.isArray(res) ? res : []);
          const mapped = (Array.isArray(list) ? list : []).map((item: any, idx: number) => {
            const mId = item.mediaId || item._id || item.id || item.fileId || `adv-search-${Date.now()}-${idx}`;
            const mFileName = item.fileName || item.name || 'image.jpg';
            const mFileUrl = item.fileUrl || item.url || item.ogFileUrl || item.compressedImgUrl || '';
            const mCompressed = item.compressedImgUrl || item.thumbnailUrl || mFileUrl;
            let mExt = item.extension || this.getFileExtension(mFileName) || '.jpg';
            if (mExt && !mExt.startsWith('.')) {
              mExt = '.' + mExt;
            }
            return {
              ...item,
              _id: mId,
              mediaId: mId,
              fileUrl: mFileUrl,
              fileName: mFileName,
              extension: mExt.toLowerCase(),
              compressedImgUrl: mCompressed,
            };
          });
          if (loadMore) {
            this.advSearchResults = [...this.advSearchResults, ...mapped];
          } else {
            this.advSearchResults = mapped;
            if (this.advTotalCount === 0 && mapped.length > 0) {
              this.advTotalCount = mapped.length;
            }
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.isAdvSearching = false;
          this.isLoadingMoreAdv = false;
          if (!loadMore) {
            this.advSearchResults = [];
            this.advTotalCount = 0;
          }
          this.advSearchError = err?.error?.message || 'Advanced search failed';
          alertToast(this.advSearchError, 'error');
          this.cdr.detectChanges();
        }
      });
    } else if (this.advSearchType === 'image') {
      if (!this.advSelectedImageFile) {
        alertToast('Please select an image to search', 'warning');
        return;
      }
      if (loadMore) {
        this.isLoadingMoreAdv = true;
      } else {
        this.isAdvSearching = true;
        this.hasPerformedAdvSearch = true;
        this.advSearchResults = [];
        this.advTotalCount = 0;
        this.advNextCursor = null;
      }
      this.advSearchError = '';

      this.vaultService.searchVaultMediaByImage(
        this.advSelectedImageFile,
        20,
        cursorToPass,
        this.advStartDate || undefined,
        this.advEndDate || undefined
      ).subscribe({
        next: (res: any) => {
          this.isAdvSearching = false;
          this.isLoadingMoreAdv = false;
          const rawData = res?.data;
          const nextCursorVal = rawData?.nextCursor ?? res?.nextCursor ?? rawData?.next_cursor ?? res?.next_cursor ?? rawData?.cursor ?? res?.cursor ?? rawData?.pagination?.nextCursor ?? res?.pagination?.nextCursor ?? null;
          this.advNextCursor = (nextCursorVal && nextCursorVal !== 'null' && nextCursorVal !== 'undefined') ? String(nextCursorVal) : null;
          if (rawData?.totalCount !== undefined && rawData?.totalCount !== null) {
            this.advTotalCount = rawData.totalCount;
          } else if (res?.totalCount !== undefined && res?.totalCount !== null) {
            this.advTotalCount = res.totalCount;
          }
          const list = rawData?.media || res?.media || rawData || (Array.isArray(res) ? res : []);
          const mapped = (Array.isArray(list) ? list : []).map((item: any, idx: number) => {
            const mId = item.mediaId || item._id || item.id || item.fileId || `adv-search-${Date.now()}-${idx}`;
            const mFileName = item.fileName || item.name || 'image.jpg';
            const mFileUrl = item.fileUrl || item.url || item.ogFileUrl || item.compressedImgUrl || '';
            const mCompressed = item.compressedImgUrl || item.thumbnailUrl || mFileUrl;
            let mExt = item.extension || this.getFileExtension(mFileName) || '.jpg';
            if (mExt && !mExt.startsWith('.')) {
              mExt = '.' + mExt;
            }
            return {
              ...item,
              _id: mId,
              mediaId: mId,
              fileUrl: mFileUrl,
              fileName: mFileName,
              extension: mExt.toLowerCase(),
              compressedImgUrl: mCompressed,
            };
          });
          if (loadMore) {
            this.advSearchResults = [...this.advSearchResults, ...mapped];
          } else {
            this.advSearchResults = mapped;
            if (this.advTotalCount === 0 && mapped.length > 0) {
              this.advTotalCount = mapped.length;
            }
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.isAdvSearching = false;
          this.isLoadingMoreAdv = false;
          if (!loadMore) {
            this.advSearchResults = [];
            this.advTotalCount = 0;
          }
          this.advSearchError = err?.error?.message || 'Advanced search failed';
          alertToast(this.advSearchError, 'error');
          this.cdr.detectChanges();
        }
      });
    }
  }

  loadMoreAdvSearch() {
    if (this.advNextCursor && this.advNextCursor !== 'null' && !this.isLoadingMoreAdv) {
      this.performAdvancedSearch(true);
    }
  }

  resetAdvancedSearch() {
    this.advTextQuery = '';
    this.advSelectedImageFile = null;
    this.advSelectedImagePreview = null;
    this.advStartDate = '';
    this.advEndDate = '';
    this.advSearchResults = [];
    this.advTotalCount = 0;
    this.advNextCursor = null;
    this.hasPerformedAdvSearch = false;
    this.isAdvSearching = false;
    this.isLoadingMoreAdv = false;
    this.advSearchError = '';
    if (this.advFileInputElement?.nativeElement) {
      this.advFileInputElement.nativeElement.value = '';
    }
  }

  clearAdvSearchResults() {
    this.hasPerformedAdvSearch = false;
    this.advSearchResults = [];
    this.advTotalCount = 0;
    this.advNextCursor = null;
    this.isAdvSearching = false;
    this.isLoadingMoreAdv = false;
    this.advSearchError = '';
  }

  formatMatchScore(score: any): string {
    if (score === undefined || score === null) return '';
    const num = Number(score);
    if (isNaN(num)) return '';
    if (num <= 1) {
      return `${Math.round(num * 100)}% match`;
    }
    return `${Math.round(num)}% match`;
  }

  previewAdvMedia(item: any) {
    if (!item) return;

    const id = item.mediaId || item._id || item.id || item.fileId;
    const fileName = item.fileName || item.name || 'image.jpg';
    const fileUrl = item.fileUrl || item.url || item.ogFileUrl || item.compressedImgUrl || '';
    const compressedImgUrl = item.compressedImgUrl || item.thumbnailUrl || fileUrl;
    let ext = item.extension || this.getFileExtension(fileName) || '.jpg';
    if (ext && !ext.startsWith('.')) {
      ext = '.' + ext;
    }

    const mediaItem = {
      ...item,
      _id: id,
      mediaId: id,
      fileUrl: fileUrl,
      fileName: fileName,
      extension: ext.toLowerCase(),
      compressedImgUrl: compressedImgUrl,
      size: item.size || 0,
      createdAt: item.createdAt || new Date().toISOString(),
    };

    let searchMediaList: any[] = [];
    if (this.advSearchResults && this.advSearchResults.length > 0) {
      searchMediaList = this.advSearchResults.map((m: any, idx: number) => {
        const mId = m.mediaId || m._id || m.id || m.fileId || `adv-result-${idx}`;
        const mFileName = m.fileName || m.name || 'image.jpg';
        const mFileUrl = m.fileUrl || m.url || m.ogFileUrl || m.compressedImgUrl || '';
        const mCompressed = m.compressedImgUrl || m.thumbnailUrl || mFileUrl;
        let mExt = m.extension || this.getFileExtension(mFileName) || '.jpg';
        if (mExt && !mExt.startsWith('.')) {
          mExt = '.' + mExt;
        }
        return {
          ...m,
          _id: mId,
          mediaId: mId,
          fileUrl: mFileUrl,
          fileName: mFileName,
          extension: mExt.toLowerCase(),
          compressedImgUrl: mCompressed,
          size: m.size || 0,
          createdAt: m.createdAt || new Date().toISOString(),
        };
      });
    } else {
      searchMediaList = [mediaItem];
    }

    this.openFullScreen(mediaItem, searchMediaList);
  }

}

